import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
// Case-insensitive multi-field job filter — shared with JobSelectionCard
// (used by PreCon/Soft Dig/Locate/CSE/Check-In/Check-Out/Pre-Trip, 2026-08-26).
import { filterJobsByQuery } from '../lib/jobSearch';
import TeamLeadReportChip from '../components/timesheet/TeamLeadReportChip';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Download, 
  Trash2, 
  Clock,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Users,
  User,
  Briefcase,
  Eye,
  Pencil,
  Timer,
  Send,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Printer,
  Settings,
  BookOpen,
  ShoppingBag,
  Wrench,
  Coffee,
  FileText,
  Sunset,
  Home,
  ShieldCheck,
  Maximize2,
  ArrowRightLeft,
  AlertTriangle,
  Shield,
  Lock,
  DollarSign,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, parseISO, getWeek, startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth, isSameWeek, isSameDay } from 'date-fns';
import { downloadCSV, parseLocalDate, getContrastTextColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shop Activity Categories - matches Time Tracker V2 (FieldTimeTrackerV2.js).
// MUST stay in sync with that list — the 'End of Day Cleanup' bug (Corey
// Parks, 2026-06-08) was caused by this array missing that entry, so manual
// timesheet edits dropped the category and the Shop Prep Total rollup
// silently undercounted it.
const SHOP_CATEGORIES = [
  { id: 'shop_prep', label: 'Shop Prep', description: 'Shop preparation & setup', icon: Settings, paid: true },
  { id: 'training', label: 'Training', description: 'Training & development', icon: BookOpen, paid: true },
  { id: 'errands', label: 'Errands', description: 'Company errands & supply runs', icon: ShoppingBag, paid: true },
  { id: 'equipment', label: 'Equipment', description: 'Equipment maintenance & prep', icon: Wrench, paid: true },
  { id: 'end_of_day_cleanup', label: 'End of Day Cleanup', description: 'End of day vehicle & equipment cleanup', icon: Sunset, paid: true },
  { id: 'lunch', label: 'Lunch', description: 'Lunch break (unpaid)', icon: Coffee, paid: false },
  { id: 'other', label: 'Other', description: 'Other activity (explain below)', icon: FileText, paid: true }
];

// Canonical set of known shop-activity titles (case/punctuation-insensitive).
// Used by isShopEntry() as a defensive third detection path so that a
// timesheet entry with title "End of Day Cleanup" is recognized as a
// shop activity even if its `shop_category` field is null AND its
// job_number doesn't start with SHOP-. This is the payroll-safety net
// that survives manual edits, legacy data, and admin re-entry.
const SHOP_TITLE_KEYS = new Set(
  SHOP_CATEGORIES
    .filter(c => c.paid)
    .map(c => c.label.toLowerCase().replace(/[^a-z0-9]/g, ''))
);

const isShopEntry = (e) => {
  if (!e) return false;
  if (e.shop_category) return true;
  const jn = String(e.job_number || '').toUpperCase();
  if (jn === 'SHOP' || jn.startsWith('SHOP-') || jn.startsWith('SHOP_') || jn.startsWith('SHOP ')) {
    return true;
  }
  // Legacy "Other" shop entries logged via desktop Add Entry before
  // 2026-06-08 stored job_number='OTHER' with no shop_category. They
  // are still paid shop activity per the user manual / mobile Tracker.
  if (jn === 'OTHER' || jn.startsWith('OTHER-') || jn.startsWith('OTHER ')) {
    return true;
  }
  // Title-based fallback for entries that lost their shop_category /
  // SHOP- prefix after a manual edit.
  const titleKey = String(e.job_title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (titleKey && SHOP_TITLE_KEYS.has(titleKey)) return true;
  return false;
};

// Compact badge label for a timesheet entry's job_number.
// Long shop activity codes like "SHOP-END_OF_DAY_CLEANUP" overflow the narrow
// badge column and visually duplicate the activity title. Collapse anything
// that's part of the Shop/Other bucket to just "SHOP" / "OTHER".
const formatJobBadge = (entry) => {
  if (!entry || !entry.job_number) return '';
  const jn = String(entry.job_number).toUpperCase();
  if (jn === 'OTHER' || jn.startsWith('OTHER-') || jn.startsWith('OTHER ')) return 'OTHER';
  if (jn.startsWith('SHOP-') || jn.startsWith('SHOP ') || jn === 'SHOP') return 'SHOP';
  return entry.job_number;
};

// Friendly Title Case for activity labels stored as raw UPPER_SNAKE_CASE
// (e.g. "END_OF_DAY_CLEANUP" -> "End of Day Cleanup").
const titleCaseLabel = (text) => {
  if (!text) return text;
  const s = String(text);
  // Only re-case if it looks like an upper-snake/upper-kebab token, otherwise
  // leave human-written titles ("CESO - Ninety-Six Storm Locate") alone.
  if (!/^[A-Z0-9_\- ]+$/.test(s) || !/[_-]/.test(s)) return s;
  const small = new Set(['of', 'and', 'or', 'the', 'a', 'an', 'in', 'on', 'to', 'for', 'from']);
  return s
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((w, i) => (i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
};

// Activity label for a timesheet entry — uses the stored job_title when
// available, falling back to a humanised job_number for legacy shop entries.
const formatEntryLabel = (entry, fallback = '—') => {
  if (!entry) return fallback;
  if (entry.job_title) return titleCaseLabel(entry.job_title);
  const jn = String(entry.job_number || '').toUpperCase();
  if (jn.startsWith('SHOP-') || jn.startsWith('OTHER-')) {
    return titleCaseLabel(jn.split('-').slice(1).join(' '));
  }
  return entry.notes ? entry.notes : fallback;
};

// Inline notes line for a timesheet entry. Renders only when notes are
// present AND they're not an exact duplicate of the activity label the
// caller is already showing (avoids "Errands / Errands" double-printing).
// Used across every entry-render surface on the timesheet (weekly card,
// daily list, manager review, signoff view, etc.).
const EntryNotesLine = ({ entry, displayedLabel = '' }) => {
  const noteStr = (entry?.notes || '').trim();
  if (!noteStr) return null;
  const labelLc = (displayedLabel || '').trim().toLowerCase();
  if (labelLc && noteStr.toLowerCase() === labelLc) return null;
  return (
    <p
      className="text-[11px] text-slate-600 italic break-words mt-0.5 leading-snug"
      title={noteStr}
      data-testid={`entry-notes-${entry.id}`}
    >
      {noteStr}
    </p>
  );
};

// Format minutes to "Xh Ym" display format
const formatMinutesToDisplay = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === 0) return '-';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

// Convert old hours format to minutes (for backward compatibility)
const hoursToMinutes = (hours) => {
  if (hours === null || hours === undefined) return 0;
  return hours * 60;
};

// Get minutes from entry (handles both old hours and new minutes format)
const getEntryMinutes = (entry, field) => {
  // Check if this is a V2 entry (has time_unit = minutes)
  if (entry.time_unit === 'minutes') {
    // V2 format: travel_minutes, work_minutes, total_minutes
    if (field === 'travel_to_job' || field === 'travel') {
      return entry.travel_minutes || 0;
    }
    if (field === 'time_on_job' || field === 'work') {
      return entry.work_minutes || 0;
    }
    if (field === 'total' || field === 'total_hours') {
      return entry.total_minutes || 0;
    }
    if (field === 'travel_from_job') {
      return 0; // V2 doesn't track this
    }
  }
  
  // Legacy format: check for _minutes suffix first
  const minutesField = `${field}_minutes`;
  if (entry[minutesField] !== undefined && entry[minutesField] !== null) {
    return entry[minutesField];
  }
  
  // Fall back to old hours format (multiply by 60)
  if (entry[field] !== undefined && entry[field] !== null) {
    return hoursToMinutes(entry[field]);
  }
  return 0;
};

// Generate hours options in 15-minute increments (0 to 12 hours)
const HOUR_OPTIONS = [];
for (let i = 0; i <= 48; i++) {
  const hours = i * 0.25;
  const label = hours === 0 ? '-' : 
    hours === 1 ? '1 hr' : 
    hours < 1 ? `${hours * 60} min` : 
    Number.isInteger(hours) ? `${hours} hrs` : 
    `${Math.floor(hours)} hr ${(hours % 1) * 60} min`;
  HOUR_OPTIONS.push({ value: hours, label });
}

// Build a label like "1h 41m" / "41m" / "2h" from an hours value (supports any minute precision).
const formatHoursLabel = (hours) => {
  const totalMin = Math.round((hours || 0) * 60);
  if (totalMin === 0) return '-';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return h === 1 ? '1 hr' : `${h} hrs`;
  return `${h} hr ${m} min`;
};

/**
 * Static "Payroll" review box — empty dashed rectangle that matches the
 * printed PDF style, sized to fit a hand-written note. Not editable;
 * payroll writes on the printout. Same height in every card so the day
 * grid stays aligned.
 */
const PayrollBox = ({ compact = false }) => (
  <div
    className={`mt-2 border border-dashed border-slate-300 rounded bg-white ${compact ? 'h-7' : 'h-8'}`}
    aria-label="Payroll review"
    data-testid="payroll-box"
  />
);

/**
 * Compact month-view widget at the top of the Timesheet page. Lets the
 * user jump to any week in any month with a single tap. Highlights the
 * currently-loaded week and "today" so it's instantly clear which week
 * the data on screen represents.
 */
const MiniMonth = ({ cursorMonth, selectedWeek, onPrevMonth, onNextMonth, onSelectDay, onJumpToToday }) => {
  const monthStart = startOfMonth(cursorMonth);
  const monthEnd = endOfMonth(cursorMonth);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = new Date();

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 max-w-sm mx-auto w-full" data-testid="timesheet-mini-month">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={onPrevMonth}
          className="p-1 rounded hover:bg-slate-200 text-slate-600"
          aria-label="Previous month"
          data-testid="mini-month-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="text-sm font-bold text-slate-900">
          {format(cursorMonth, 'MMMM yyyy')}
        </p>
        <button
          type="button"
          onClick={onNextMonth}
          className="p-1 rounded hover:bg-slate-200 text-slate-600"
          aria-label="Next month"
          data-testid="mini-month-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[10px] uppercase tracking-wide text-slate-400 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          const inMonth = isSameMonth(d, cursorMonth);
          const inSelectedWeek = isSameWeek(d, selectedWeek);
          const isToday = isSameDay(d, today);
          const cls = [
            'text-xs rounded h-7 w-full flex items-center justify-center transition-colors',
            inMonth ? 'text-slate-700' : 'text-slate-300',
            inSelectedWeek ? 'bg-amber-100 ring-1 ring-amber-300 font-bold text-amber-900' : 'hover:bg-slate-200',
            isToday && !inSelectedWeek ? 'ring-1 ring-amber-400' : '',
          ].join(' ');
          return (
            <button
              key={format(d, 'yyyy-MM-dd')}
              type="button"
              onClick={() => onSelectDay(d)}
              className={cls}
              data-testid={`mini-month-day-${format(d, 'yyyy-MM-dd')}`}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px]">
        <span className="text-slate-400">Tap any day to load that week</span>
        <button
          type="button"
          onClick={onJumpToToday}
          className="text-amber-600 hover:text-amber-700 font-semibold"
          data-testid="mini-month-today"
        >
          Today
        </button>
      </div>
    </div>
  );
};

/**
 * Return HOUR_OPTIONS plus, when the supplied value isn't already in the list,
 * a dynamically inserted option for that exact value. Keeps the dropdown sorted
 * and prevents auto-generated minute-precision values (e.g. 41 min, 3 min) from
 * rendering blank in the Edit Time Entry dialog.
 */
const getHourOptionsForValue = (rawValue) => {
  const v = Number(rawValue);
  if (!Number.isFinite(v) || v <= 0) return HOUR_OPTIONS;
  const exists = HOUR_OPTIONS.some(opt => Math.abs(opt.value - v) < 1e-9);
  if (exists) return HOUR_OPTIONS;
  const custom = { value: v, label: `${formatHoursLabel(v)} (auto-tracked)` };
  return [...HOUR_OPTIONS, custom].sort((a, b) => a.value - b.value);
};

export default function Timesheet() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_user';
  const isSuperUser = user?.role === 'super_user';
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    () => (user?.role === 'admin' || user?.role === 'super_user') ? 'team-timesheets' : 'my-timesheet'
  );
  const [entries, setEntries] = useState([]);
  const [teamSummary, setTeamSummary] = useState([]);
  const [teamEntries, setTeamEntries] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  // When TRUE, the team-timesheets tab hides the employee grid and shows
  // only the selected member's detail panel. Triggered by a deep-link from
  // the Approval Dashboard or Wall View (`/timesheet?user=...&week=...`)
  // so approval flow lands directly on the entries that need attention.
  const [memberFocusMode, setMemberFocusMode] = useState(false);
  // Payroll Invoice # — typed live on the page while reviewing a member's
  // week, then printed verbatim into the per-member PDF box. Resets when
  // switching to a different team member so we never carry a stale value
  // across employees.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  // Weekly envelope for the currently-viewed team member in focus mode.
  // Mirrors `weeklyTimesheet` (which is the admin's own envelope) but for the
  // selected user/week so the focus-mode banner can show status + Approve/Reject.
  const [selectedMemberWeekly, setSelectedMemberWeekly] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]); // All jobs for manager view
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Search query for the Job/Activity dropdown inside Add/Edit dialogs.
  // Filters across job_number, job_title, client_name, and service_address.
  // One shared state — the two dialogs are mutually exclusive.
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [showDetailDialog, setShowDetailDialog] = useState(false); // For viewing timesheet details
  const [showResetConfirm, setShowResetConfirm] = useState(false); // For reset week confirmation
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  // Show/hide job-number badges across this page. OFF by default — owner
  // payroll review found the J-number pills distracting and asked for
  // them to be hidden by default with an opt-in toggle. Persisted in
  // localStorage under the SAME key used by TimesheetMemberPreview so
  // the user only flips it once for both views.
  const [showJobNumbers, setShowJobNumbers] = useState(() => {
    try { return localStorage.getItem('tsmp_show_job_numbers') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tsmp_show_job_numbers', showJobNumbers ? '1' : '0'); } catch {}
  }, [showJobNumbers]);
  // Mini-month nav: which month the small calendar widget is currently
  // showing. Independent from selectedWeek so users can browse months
  // without thrashing the loaded week's data until they click a day.
  const [miniMonthCursor, setMiniMonthCursor] = useState(new Date());
  const [addingForMember, setAddingForMember] = useState(null); // For manager adding entry for team member
  // Whether the Team Timesheets grid should surface deactivated employees.
  // OFF by default — keeps the active roster clean. Admins can flip it on
  // when reviewing historical weeks where an inactive employee still had
  // entries. Persisted in localStorage so the choice survives page reloads.
  const [showInactiveMembers, setShowInactiveMembers] = useState(() => {
    try { return localStorage.getItem('ts_show_inactive_members') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('ts_show_inactive_members', showInactiveMembers ? '1' : '0'); } catch {}
  }, [showInactiveMembers]);

  // Reset the Job/Activity search box every time the Add or Edit
  // dialog opens, so the previous search doesn't leak into a fresh
  // entry creation.
  useEffect(() => {
    if (showAddDialog || showEditDialog) {
      setJobSearchQuery('');
    }
  }, [showAddDialog, showEditDialog]);
  
  // Submission state
  const [weeklyTimesheet, setWeeklyTimesheet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  // 'day' = original card stack; 'week' = spreadsheet roll-up
  const [myTimesheetViewMode, setMyTimesheetViewModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('timesheet.myViewMode');
      return saved === 'week' || saved === 'day' ? saved : 'day';
    } catch { return 'day'; }
  });
  const setMyTimesheetViewMode = (m) => {
    setMyTimesheetViewModeState(m);
    try { localStorage.setItem('timesheet.myViewMode', m); } catch {}
  };
  // 'calendar' = Sun→Sat day cards (default — matches "My Timesheet" Day View);
  // 'grid' = weekly job × day pivot table; 'table' = flat entry list.
  const [memberViewMode, setMemberViewModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('timesheet.memberViewMode');
      if (saved === 'table' || saved === 'grid' || saved === 'calendar') return saved;
      return 'calendar';
    } catch { return 'calendar'; }
  });
  const setMemberViewMode = (m) => {
    setMemberViewModeState(m);
    try { localStorage.setItem('timesheet.memberViewMode', m); } catch {}
  };

  const [newEntry, setNewEntry] = useState({
    job_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    travel_to_job: '',
    time_on_job: '',
    travel_from_job: '',
    notes: '',
    custom_job_description: '',
    for_user_id: '',
    pay_type: 'Regular',
  });

  // Handle URL parameters from calendar "Log Time" button
  useEffect(() => {
    const jobId = searchParams.get('job_id');
    const jobNumber = searchParams.get('job_number');
    const jobTitle = searchParams.get('job_title');
    const dateParam = searchParams.get('date');
    // Approval-dashboard deep-link: /timesheet?user=<id>&week=YYYY-MM-DD
    const userParam = searchParams.get('user');
    const weekParam = searchParams.get('week');
    // Feb-2026: /timesheet?tab=team-timesheets is used by
    // TimesheetMemberPreview's Back button so the user lands back on
    // the tile list (not their own timesheet). Managers only — the
    // guard matches the `userParam` handling below.
    const tabParam = searchParams.get('tab');

    // 1) Jump the calendar to the requested week (also used in isolation
    //    when only `week=` is provided).
    let parsedWeekDate = null;
    if (weekParam) {
      try {
        parsedWeekDate = parseISO(weekParam);
        setSelectedWeek(parsedWeekDate);
        setMiniMonthCursor(parsedWeekDate);
      } catch (e) { /* ignore bad week param */ }
    }

    // 2) View another user's timesheet. Only managers/admins land on the
    //    team tab — non-admins ignore the param so they can't peek at
    //    someone else's data via URL.
    if (userParam && isManager) {
      setActiveTab('team-timesheets');
      // Enter focus mode so the grid is hidden and only the selected
      // member's entries are shown — approval flow lands directly here.
      setMemberFocusMode(true);
      // Default to the Calendar view so managers see the same Sun→Sat day-card
      // calendar that workers see on their own timesheet — much easier to scan
      // than the reverse-chronological flat list. Per-entry edit/delete/lock
      // affordances are still available from each day card.
      setMemberViewMode('calendar');
      // Match by id; if we don't have the list yet, set a minimal stub so
      // the right-hand panel still renders while the real one loads.
      setSelectedMember(prev => prev?.user_id === userParam ? prev : { user_id: userParam, user_name: '' });
      // Pass the parsed week explicitly because setSelectedWeek is async and
      // would otherwise let these fetchers read a stale `selectedWeek` from
      // closure (root cause of "31.5h · 0 entries" bug in iter 60).
      fetchMemberEntries(userParam, parsedWeekDate);
      fetchMemberWeekly(userParam, parsedWeekDate);
      // Clear so refresh/back doesn't reload someone else's data.
      setSearchParams({});
      return;
    }

    // Non-manager landed here with a `?user=` param — silently strip it
    // so the URL bar matches what they actually see (their own timesheet).
    if (userParam && !isManager) {
      setSearchParams({});
    }

    // Feb-2026: honor `?tab=team-timesheets` from the preview page's
    // Back button. Manager-only; non-managers get the URL stripped.
    if (tabParam === 'team-timesheets' && !userParam) {
      if (isManager) {
        setActiveTab('team-timesheets');
      }
      setSearchParams({});
    }

    if (jobId) {
      // Pre-fill the new entry form with job data
      setNewEntry(prev => ({
        ...prev,
        job_id: jobId,
        date: dateParam || format(new Date(), 'yyyy-MM-dd')
      }));
      
      // Set the week to the date parameter if provided
      if (dateParam) {
        try {
          const parsedDate = parseISO(dateParam);
          setSelectedWeek(parsedDate);
        } catch (e) {
          console.error('Failed to parse date:', e);
        }
      }
      
      // Open the add dialog
      setShowAddDialog(true);
      
      // Show toast notification
      toast.info(`Logging time for: ${jobTitle || 'Job'}`);
      
      // Clear URL parameters to prevent re-triggering
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, isManager]);

  useEffect(() => {
    fetchTimesheets();
    fetchJobs();
    fetchWeeklyTimesheetStatus();
    // team list is needed for the per-member calendar_color used by the
    // initial bubbles on My Timesheet Day View — fetch for every user,
    // not just managers.
    fetchTeamMembers();
    if (isManager) {
      fetchTeamSummary();
      fetchAllJobs();
      // Re-fetch the currently-viewed member's entries when the week changes
      // so the right-hand panel doesn't go stale after week navigation.
      if (selectedMember?.user_id) {
        fetchMemberEntries(selectedMember.user_id);
        fetchMemberWeekly(selectedMember.user_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek, isManager, showInactiveMembers]);

  const fetchTimesheets = async () => {
    try {
      const start = format(startOfWeek(selectedWeek), 'yyyy-MM-dd');
      const end = format(endOfWeek(selectedWeek), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/timesheets/my?start_date=${start}&end_date=${end}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyTimesheetStatus = async () => {
    try {
      const weekNum = getWeek(selectedWeek);
      const year = selectedWeek.getFullYear();
      const response = await axios.get(`${API}/timesheets/weekly`);
      const found = response.data.find(ts => ts.week_number === weekNum && ts.year === year);
      setWeeklyTimesheet(found || null);
    } catch (error) {
      console.error('Failed to fetch weekly timesheet status:', error);
    }
  };

  const handleSubmitTimesheet = async () => {
    if (entries.length === 0) {
      toast.error('Add time entries before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      const weekNum = getWeek(selectedWeek);
      const year = selectedWeek.getFullYear();
      await axios.post(`${API}/timesheets/weekly/submit?week_number=${weekNum}&year=${year}`);
      toast.success('Timesheet submitted for approval!');
      fetchWeeklyTimesheetStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs/my`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  const fetchAllJobs = async () => {
    try {
      const fields = 'id,job_number,job_title,client_name,contact_name,service_address,service_city,service_state,service_type,date,status';
      const response = await axios.get(`${API}/jobs?fields=${fields}`);
      setAllJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch all jobs:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
      // If we were waiting on a stub (deep-link from approval dashboard),
      // hydrate it with the full record once we have the list.
      setSelectedMember(prev => {
        if (!prev || prev.user_name) return prev;
        const match = (response.data || []).find(m => (m.id || m.user_id) === prev.user_id);
        if (!match) return prev;
        return {
          ...prev,
          user_name: match.full_name || match.name || match.user_name || prev.user_id,
          calendar_color: match.calendar_color,
          role: match.role,
        };
      });
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchTeamSummary = async () => {
    try {
      const start = format(startOfWeek(selectedWeek), 'yyyy-MM-dd');
      const end = format(endOfWeek(selectedWeek), 'yyyy-MM-dd');
      const inactiveFlag = showInactiveMembers ? '&include_inactive=true' : '';
      const response = await axios.get(`${API}/timesheets/team/summary?start_date=${start}&end_date=${end}${inactiveFlag}`);
      setTeamSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch team summary:', error);
    }
  };

  const fetchMemberEntries = async (userId, weekDate = null) => {
    try {
      // When deep-linking from /office or /timesheet/approval, the URL carries
      // ?week= which we ALSO push into selectedWeek via setSelectedWeek. That
      // state update is async, so passing weekDate explicitly avoids a stale
      // closure here on the very first call.
      const refWeek = weekDate || selectedWeek;
      const start = format(startOfWeek(refWeek), 'yyyy-MM-dd');
      const end = format(endOfWeek(refWeek), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/timesheets?user_id=${userId}&start_date=${start}&end_date=${end}`);
      setTeamEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch member entries:', error);
      toast.error('Failed to load entries');
    }
  };

  // Fetch the weekly envelope (status, submitted_at, approved_by, etc.) for the
  // team member currently being viewed. Powers the focus-mode banner so the
  // admin sees the same workflow info the old Office Dashboard modal showed.
  const fetchMemberWeekly = async (userId, weekDate = null) => {
    if (!userId) {
      setSelectedMemberWeekly(null);
      return;
    }
    try {
      const refWeek = weekDate || selectedWeek;
      const weekNum = getWeek(refWeek);
      const year = refWeek.getFullYear();
      // Admin endpoint returns every user's weeklies in one shot, so filter client-side.
      const res = await axios.get(`${API}/timesheets/weekly?all_users=true`);
      const match = (res.data || []).find(
        ts => ts.user_id === userId && ts.week_number === weekNum && ts.year === year
      );
      setSelectedMemberWeekly(match || null);
    } catch (e) {
      console.error('Failed to fetch member weekly:', e);
      setSelectedMemberWeekly(null);
    }
  };

  const handleViewMemberTimesheet = (member) => {
    setSelectedMember(member);
    fetchMemberEntries(member.user_id);
    fetchMemberWeekly(member.user_id);
  };

  const handleAddEntryForMember = (member) => {
    setAddingForMember(member);
    setNewEntry({
      job_id: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      travel_to_job: '',
      time_on_job: '',
      travel_from_job: '',
      notes: '',
      custom_job_description: '',
      for_user_id: member.user_id
    });
    setShowAddDialog(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    // Convert each field to HOURS at full precision. V2 auto-tracked entries store
    // minutes in legacy hour-named fields, so we read via the minute helper and divide.
    const toHours = (field) => {
      const minutes = getEntryMinutes(entry, field);
      return minutes ? Number((minutes / 60).toFixed(4)) : 0;
    };
    // Figure out which dropdown value to preselect when editing.
    //   - Real job entry  -> use entry.job_id
    //   - Shop entry      -> use the shop_category (e.g., 'shop_prep'),
    //                        which matches an item in SHOP_CATEGORIES
    //   - Legacy / unknown -> fall back to 'other'
    const isShopEntryRow = isShopEntry(entry);
    const editJobId = entry.job_id
      ? entry.job_id
      : (entry.shop_category && SHOP_CATEGORIES.some(c => c.id === entry.shop_category)
          ? entry.shop_category
          : (isShopEntryRow ? 'other' : 'other'));

    // For "Other" the description IS the job title; for any other shop
    // category the description was stored in `notes` on save, so pull
    // it back out into the input so the user sees what they wrote
    // before. For real jobs, no description field is shown.
    let editDescription = '';
    if (editJobId === 'other') {
      editDescription = entry.job_title || '';
    } else if (SHOP_CATEGORIES.some(c => c.id === editJobId)) {
      editDescription = entry.notes || '';
    }

    setNewEntry({
      job_id: editJobId,
      date: entry.date,
      travel_to_job: toHours('travel_to_job'),
      time_on_job: toHours('time_on_job'),
      travel_from_job: toHours('travel_from_job'),
      // For non-Other shop entries we move the description out of notes
      // into custom_job_description so the user can edit it in the new
      // prominent field. The save path puts it back into notes.
      notes: (editJobId !== 'other' && SHOP_CATEGORIES.some(c => c.id === editJobId))
        ? ''
        : (entry.notes || ''),
      custom_job_description: editDescription,
      for_user_id: entry.user_id
    });
    setShowEditDialog(true);
  };

  // Focus-mode workflow actions — let the admin Approve / Reject / Reopen / Mark-Paid
  // the currently-viewed member's week without leaving the page. These reuse the
  // exact endpoints the Approval Dashboard already calls.
  const handleFocusApprove = async () => {
    if (!selectedMemberWeekly?.id) return;
    try {
      await axios.post(`${API}/timesheets/weekly/${selectedMemberWeekly.id}/approve`);
      toast.success('Week approved');
      if (selectedMember?.user_id) fetchMemberWeekly(selectedMember.user_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const handleFocusReject = async () => {
    if (!selectedMemberWeekly?.id) return;
    const reason = window.prompt('Reason for sending this week back?');
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(`${API}/timesheets/weekly/${selectedMemberWeekly.id}/reject`, { reason: reason.trim() });
      toast.success('Week sent back to employee');
      if (selectedMember?.user_id) fetchMemberWeekly(selectedMember.user_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const handleFocusReopen = async () => {
    if (!selectedMemberWeekly?.id) return;
    const reason = window.prompt('Reason for reopening this approved/paid week?');
    if (!reason || !reason.trim()) return;
    try {
      await axios.post(`${API}/timesheets/weekly/${selectedMemberWeekly.id}/reopen`, { reason: reason.trim() });
      toast.success('Week reopened');
      if (selectedMember?.user_id) fetchMemberWeekly(selectedMember.user_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reopen failed');
    }
  };

  const handleFocusMarkPaid = async () => {
    if (!selectedMemberWeekly?.id) return;
    if (!window.confirm('Mark this week as Paid? This will hard-lock the entries.')) return;
    try {
      await axios.post(`${API}/timesheets/weekly/${selectedMemberWeekly.id}/mark-paid`);
      toast.success('Week marked Paid');
      if (selectedMember?.user_id) fetchMemberWeekly(selectedMember.user_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mark paid failed');
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.job_id) {
      toast.error('Please select a job or activity');
      return;
    }
    
    // Check if this is a shop category
    const shopCategory = SHOP_CATEGORIES.find(cat => cat.id === newEntry.job_id);
    
    // Validate custom description if "Other" shop activity is selected
    if (newEntry.job_id === 'other' && !newEntry.custom_job_description?.trim()) {
      toast.error('Please describe the activity');
      return;
    }

    // Prepare entry data
    const entryData = {
      ...newEntry,
      travel_to_job: parseFloat(newEntry.travel_to_job) || 0,
      time_on_job: parseFloat(newEntry.time_on_job) || 0,
      travel_from_job: parseFloat(newEntry.travel_from_job) || 0
    };
    
    // Handle shop categories (non-job entries)
    if (shopCategory) {
      entryData.job_id = null;
      // Explicit shop_category field — primary signal used by the Shop
      // Prep Total rollup. Survives manual edits even if job_number
      // somehow gets changed.
      entryData.shop_category = shopCategory.id;
      if (newEntry.job_id === 'other') {
        entryData.job_title = newEntry.custom_job_description;
        entryData.job_number = 'OTHER';
      } else {
        entryData.job_title = shopCategory.label;
        // Use SHOP-<UPPER_SNAKE> prefix to match the Time Tracker v2
        // backend's send-to-timesheet output (e.g. "SHOP-END_OF_DAY_CLEANUP").
        entryData.job_number = `SHOP-${shopCategory.id.toUpperCase()}`;
        // If the worker described their activity, push that description
        // into the entry's notes so it shows up on the timesheet preview
        // and reports. We keep the canonical job_title ("Shop Prep" etc.)
        // so that grouping/rollup logic still works correctly.
        const desc = (newEntry.custom_job_description || '').trim();
        if (desc) {
          entryData.notes = entryData.notes
            ? `${desc} — ${entryData.notes}`
            : desc;
        }
      }
      // Mark lunch as unpaid in notes if applicable
      if (newEntry.job_id === 'lunch' && !entryData.notes) {
        entryData.notes = 'Unpaid break';
      }
    }

    setSaving(true);
    try {
      await axios.post(`${API}/timesheets`, entryData);
      toast.success('Time entry added');
      setShowAddDialog(false);
      setAddingForMember(null);
      setNewEntry({
        job_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        travel_to_job: '',
        time_on_job: '',
        travel_from_job: '',
        notes: '',
        custom_job_description: '',
        for_user_id: ''
      });
      fetchTimesheets();
      // Refresh team data if we added for another member
      if (entryData.for_user_id) {
        fetchTeamSummary();
        if (selectedMember && selectedMember.user_id === entryData.for_user_id) {
          fetchMemberEntries(selectedMember.user_id);
        }
      }
    } catch (error) {
      toast.error('Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEntry = async () => {
    if (!newEntry.job_id) {
      toast.error('Please select a job or activity');
      return;
    }
    
    // Check if this is a shop category
    const shopCategory = SHOP_CATEGORIES.find(cat => cat.id === newEntry.job_id);
    
    if (newEntry.job_id === 'other' && !newEntry.custom_job_description?.trim()) {
      toast.error('Please describe the activity');
      return;
    }

    const entryData = {
      ...newEntry,
      travel_to_job: parseFloat(newEntry.travel_to_job) || 0,
      time_on_job: parseFloat(newEntry.time_on_job) || 0,
      travel_from_job: parseFloat(newEntry.travel_from_job) || 0
    };
    
    // Handle shop categories (non-job entries)
    if (shopCategory) {
      entryData.job_id = null;
      // Explicit shop_category — survives future manual edits.
      entryData.shop_category = shopCategory.id;
      if (newEntry.job_id === 'other') {
        entryData.job_title = newEntry.custom_job_description;
        entryData.job_number = 'OTHER';
      } else {
        entryData.job_title = shopCategory.label;
        entryData.job_number = `SHOP-${shopCategory.id.toUpperCase()}`;
        // Push the worker's description into notes (mirrors Add Entry).
        const desc = (newEntry.custom_job_description || '').trim();
        if (desc) {
          entryData.notes = entryData.notes
            ? `${desc} — ${entryData.notes}`
            : desc;
        }
      }
      if (newEntry.job_id === 'lunch' && !entryData.notes) {
        entryData.notes = 'Unpaid break';
      }
    }

    setSaving(true);
    try {
      await axios.put(`${API}/timesheets/${editingEntry.id}`, entryData);
      toast.success('Time entry updated');
      setShowEditDialog(false);
      setEditingEntry(null);
      setNewEntry({
        job_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        travel_to_job: '',
        time_on_job: '',
        travel_from_job: '',
        notes: '',
        custom_job_description: '',
        for_user_id: ''
      });
      fetchTimesheets();
      // Refresh team data
      fetchTeamSummary();
      if (selectedMember) {
        fetchMemberEntries(selectedMember.user_id);
      }
    } catch (error) {
      toast.error('Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId, userId = null) => {
    // Confirm before deleting — workers on mobile were hitting Delete
    // instead of the adjacent Edit button and losing entries with no
    // way back (2026-09-08 owner report).
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/timesheets/${entryId}`);
      toast.success('Entry deleted');
      fetchTimesheets();
      // Refresh team data if deleting from team view
      if (userId) {
        fetchTeamSummary();
        if (selectedMember && selectedMember.user_id === userId) {
          fetchMemberEntries(selectedMember.user_id);
        }
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleExportCSV = () => {
    if (entries.length === 0) {
      toast.error('No time entries to export');
      return;
    }
    const exportData = entries.map(entry => ({
      Date: entry.date,
      'Job #': entry.job_number || '-',
      'Client': entry.client_name || '-',
      'Job Title': entry.job_title || 'N/A',
      'Contact': entry.contact_name || '-',
      'Team': entry.team_initials || '-',
      'Travel To (hrs)': (getEntryMinutes(entry, 'travel_to_job') / 60).toFixed(2),
      'Time On Job (hrs)': (getEntryMinutes(entry, 'time_on_job') / 60).toFixed(2),
      'Travel From (hrs)': (getEntryMinutes(entry, 'travel_from_job') / 60).toFixed(2),
      'Total Hours': (getEntryMinutes(entry, 'total') / 60).toFixed(2),
      Notes: entry.notes || ''
    }));
    downloadCSV(exportData, `timesheet-${format(selectedWeek, 'yyyy-MM-dd')}.csv`);
    toast.success('CSV downloaded');
  };

  // Team Export Functions
  const handleExportTeamCSV = () => {
    if (teamSummary.length === 0) {
      toast.error('No team data to export');
      return;
    }
    
    // Export team summary data
    const exportData = teamSummary.map(member => ({
      'Employee': member.user_name,
      'Email': member.user_email,
      'Role': member.role,
      'Travel Hours': member.travel_hours.toFixed(2),
      'On Job Hours': member.on_job_hours.toFixed(2),
      'Total Hours': member.total_hours.toFixed(2),
      'Entry Count': member.entry_count
    }));
    
    // Add total row
    exportData.push({
      'Employee': 'TEAM TOTAL',
      'Email': '',
      'Role': '',
      'Travel Hours': teamSummary.reduce((sum, m) => sum + m.travel_hours, 0).toFixed(2),
      'On Job Hours': teamSummary.reduce((sum, m) => sum + m.on_job_hours, 0).toFixed(2),
      'Total Hours': teamSummary.reduce((sum, m) => sum + m.total_hours, 0).toFixed(2),
      'Entry Count': teamSummary.reduce((sum, m) => sum + m.entry_count, 0)
    });
    
    downloadCSV(exportData, `team-timesheet-week${getWeek(weekStart)}-${format(weekStart, 'yyyy-MM-dd')}.csv`);
    toast.success('Team timesheet CSV downloaded');
  };

  // Team summary print view. Navigates to a dedicated React route
  // (/timesheet/team/:weekStart/preview) which renders the same
  // table the user sees on screen and calls window.print() from the
  // SAME tab. Replaces the legacy window.open() + document.write()
  // flow which blanked the print preview in Safari and was flaky in
  // modern Chrome/Edge due to cross-origin / about:blank tightening.
  const handlePrintTeamView = () => {
    if (teamSummary.length === 0) {
      toast.error('No team data to print');
      return;
    }
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    navigate(`/timesheet/team/${weekStartStr}/preview`);
  };

  // Per-member print view used when an admin is in member-focus mode on
  // the approval page. Renders the EXACT same Sun→Sat calendar grid PLUS
  // the per-day Time Entries list that's on screen, so office staff
  // verifying entries into QuickBooks see one printable page that mirrors
  // what the employee saw on submission.
  //
  // Implementation note: we hand the new tab a Blob URL instead of using
  // document.write(). Safari has a long-standing bug where windows opened
  // with document.write() get their DOM detached the moment window.print()
  // fires — which blanked the source tab the moment users hit Print/Save.
  // A Blob URL gives the new tab a real HTML document with its own URL
  // (blob:https://…/<uuid>) that survives the print dialog.
  // Per-member print/PDF view. We navigate to a dedicated React route
  // (/timesheet/member/:userId/week/:weekStart/preview) which renders the
  // same calendar layout the user approved on screen, then calls
  // window.print() from the SAME tab. This is the exact pattern used by
  // PreConPreview / LocatePreview / TimesheetPreview and avoids Safari's
  // bug where window.open() + document.write() + window.print() blanks
  // the print preview into a white "Untitled" page.
  const handlePrintMemberView = () => {
    const member = selectedMember;
    if (!member) {
      toast.error('No team member selected');
      return;
    }
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    // Preserve the Team Timesheets tab across the round-trip — Back
    // in the preview reads `back_to_tab` and returns here instead of
    // dropping the user on /timesheet mine. (Feb-2026 owner request.)
    const parts = [`back_to_tab=team-timesheets`];
    if (invoiceNumber) parts.push(`invoice=${encodeURIComponent(invoiceNumber)}`);
    const params = `?${parts.join('&')}`;
    navigate(`/timesheet/member/${member.user_id || member.id}/week/${weekStartStr}/preview${params}`);
  };

  const weekStart = startOfWeek(selectedWeek);
  const weekEnd = endOfWeek(selectedWeek);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getEntriesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Sort morning → end of day so the per-day cards in the week grid read
    // top-down like a calendar. Time Tracker entries have a start_time;
    // manual entries (no start_time) fall to the bottom via the '9999'
    // sentinel. This applies to EVERY user (My Timesheet day view, focus
    // mode calendar view, and the day-detail dialog).
    return entries
      .filter(e => e.date === dateStr)
      .sort((a, b) =>
        (a.start_time || a.created_at || '9999')
          .localeCompare(b.start_time || b.created_at || '9999')
      );
  };

  const totalMinutes = entries.reduce((sum, e) => sum + getEntryMinutes(e, 'total'), 0);
  const teamTotalMinutes = teamSummary.reduce((sum, m) => sum + (m.total_minutes || hoursToMinutes(m.total_hours) || 0), 0);

  // Map of initials (e.g. "CH") → calendar_color so the team-member
  // bubbles on the Day View match the avatar colors used elsewhere in
  // the app (Layout header, Calendar, PDF preview). Derived from the
  // already-loaded teamMembers list — same algorithm as
  // TimesheetMemberPreview.js so the keys line up with the
  // comma-separated `team_initials` stored on each entry.
  const colorByInitials = React.useMemo(() => {
    const map = {};
    for (const u of (teamMembers || [])) {
      const name = u?.full_name || u?.user_name || '';
      const color = u?.calendar_color;
      if (!name || !color) continue;
      const parts = name.trim().split(/\s+/);
      const ini = ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
      if (ini) map[ini] = color;
    }
    return map;
  }, [teamMembers]);

  // Inline-reassign popover used in the Time Entries table — friction #5.
  // Lets the office swap an entry's job (or move it to SHOP/OTHER) without
  // opening the full edit dialog.
  const ReassignPopover = ({ entry }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const filtered = (jobs || []).filter(j => {
      if (!q) return true;
      const s = q.toLowerCase();
      return (j.job_number || '').toLowerCase().includes(s)
        || (j.job_title || '').toLowerCase().includes(s)
        || (j.client_name || '').toLowerCase().includes(s);
    }).slice(0, 12);
    const reassign = async (target) => {
      try {
        await axios.patch(`${API}/timesheets/${entry.id}/reassign-job`, target);
        toast.success('Entry reassigned');
        setOpen(false);
        fetchTimesheets();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Reassign failed');
      }
    };
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-300 hover:text-amber-600"
            title="Reassign to another job"
            data-testid={`reassign-${entry.id}`}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2" align="end">
          <p className="text-xs text-slate-500 px-2 pt-1">Reassign this entry</p>
          <Input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search job # / title / client…"
            className="my-2 h-9"
            data-testid="reassign-search"
          />
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {filtered.map(j => (
              <button
                key={j.id}
                onClick={() => reassign({ job_id: j.id })}
                className="w-full text-left px-2 py-2 hover:bg-amber-50 rounded text-sm"
                data-testid={`reassign-option-${j.id}`}
              >
                <p className="font-mono text-[11px] font-bold text-amber-600">{j.job_number}</p>
                <p className="font-medium text-slate-900 truncate">{j.job_title}</p>
                {j.client_name && <p className="text-xs text-slate-500 truncate">{j.client_name}</p>}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-2 py-3 text-xs text-slate-400">No jobs match</p>
            )}
          </div>
          <div className="border-t border-slate-100 mt-2 pt-2 px-2">
            <button
              onClick={() => reassign({ job_id: null, job_number: 'OTHER', job_title: 'Other' })}
              className="text-xs text-slate-500 hover:text-amber-600 font-medium"
            >
              → Move to SHOP / OTHER bucket
            </button>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Render function for My Timesheet content
  const renderMyTimesheetContent = () => {
    if (myTimesheetViewMode === 'week') {
      return renderMyWeekSpreadsheet();
    }
    return (
    <>
      {/* Returned week banner — friction #9.
          Big and unmissable when Sarah returns the week with a reason. */}
      {weeklyTimesheet?.status === 'Draft' && weeklyTimesheet?.rejection_reason && (
        <div
          className="border-l-4 border-amber-500 bg-amber-50 rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="returned-week-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-amber-900">
              {weeklyTimesheet.rejected_by_name
                ? `${weeklyTimesheet.rejected_by_name} returned this week`
                : 'This week was returned'}
            </p>
            <p className="text-sm text-amber-800 mt-0.5">
              <span className="font-medium">Reason:</span> {weeklyTimesheet.rejection_reason}
            </p>
            <p className="text-xs text-amber-700 mt-1">Fix the entries below, then click <strong>Submit Week</strong> again.</p>
          </div>
        </div>
      )}

      {/* Reopened week banner — friction #7. */}
      {weeklyTimesheet?.status === 'Submitted' && weeklyTimesheet?.reopen_reason && (
        <div
          className="border-l-4 border-blue-500 bg-blue-50 rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="reopened-week-banner"
        >
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-blue-900">
              {weeklyTimesheet.reopened_by ? `${weeklyTimesheet.reopened_by} reopened this week` : 'This week was reopened'}
            </p>
            <p className="text-sm text-blue-800 mt-0.5">
              <span className="font-medium">Reason:</span> {weeklyTimesheet.reopen_reason}
            </p>
          </div>
        </div>
      )}

      {/* Sticky Submit-Week bar — friction items #1 + #2.
          Visible whenever this week's status is Draft (or no record yet).
          Shows: countdown to Friday 8pm, entry count, big amber Submit. */}
      {(() => {
        const status = weeklyTimesheet?.status
          || (entries.length > 0 ? 'Draft' : null);
        const showBar = status === 'Draft' || (!status && entries.length === 0);
        if (!showBar) return null;
        // Friday 8pm local for the active week
        const fri = new Date(weekStart);
        fri.setDate(fri.getDate() + 5); // Sun=0 → Fri=5
        fri.setHours(20, 0, 0, 0);
        const now = new Date();
        const diffMs = fri.getTime() - now.getTime();
        const past = diffMs < 0;
        const absH = Math.floor(Math.abs(diffMs) / 3600000);
        const absM = Math.floor((Math.abs(diffMs) % 3600000) / 60000);
        const deadlineLabel = past
          ? `${absH}h ${absM}m past Fri 8pm`
          : (absH > 24
            ? `Due ${format(fri, 'EEE h:mma')} (${Math.floor(absH / 24)}d ${absH % 24}h)`
            : `Due ${format(fri, 'EEE h:mma')} (${absH}h ${absM}m)`);
        const tone = past
          ? 'bg-red-50 border-red-200 text-red-800'
          : absH < 8
            ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-slate-50 border-slate-200 text-slate-700';
        return (
          <div
            className={`sticky top-2 z-30 -mx-1 px-4 py-3 rounded-lg border ${tone} flex items-center justify-between gap-3 flex-wrap shadow-sm`}
            data-testid="submit-week-bar"
          >
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <Clock className={`w-5 h-5 ${past ? 'text-red-600' : 'text-amber-600'}`} />
              <div>
                <p className="text-sm font-semibold leading-tight">
                  {past ? '⚠ Timesheet past deadline' : 'Submit your week'}
                </p>
                <p className="text-xs opacity-80">
                  {deadlineLabel} · {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · {formatMinutesToDisplay(totalMinutes)}
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubmitTimesheet}
              disabled={entries.length === 0}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
              data-testid="submit-week-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Submit Week
            </Button>
          </div>
        );
      })()}

      {/* Weekly View */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        {weekDays.map(day => {
          const dayEntries = getEntriesForDate(day);
          const dayTotal = dayEntries.reduce((sum, e) => sum + getEntryMinutes(e, 'total'), 0);
          // Per-day Shop Prep total — payroll wanted a single roll-up line
          // for shop/prep time per day so they don't have to add SHOP-*
          // entries by hand. Only rendered when there's any shop time on
          // the day. `isShopEntry` matches both SHOP-* job_numbers and
          // shop_category entries.
          const shopMinutes = dayEntries.reduce(
            (s, e) => (isShopEntry(e) ? s + getEntryMinutes(e, 'total') : s),
            0,
          );
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

          return (
            <Card 
              key={day.toISOString()} 
              className={`border-slate-200 ${isToday ? 'border-amber-500 bg-amber-50/50' : ''}`}
            >
              <CardHeader className="p-3 pb-2">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className={`text-xs uppercase tracking-wide font-semibold ${isToday ? 'text-amber-600' : 'text-slate-500'}`}>
                      {format(day, 'EEE')}
                    </p>
                    <p className={`text-2xl font-bold font-['Barlow_Condensed'] leading-none ${isToday ? 'text-amber-600' : 'text-slate-900'}`}>
                      {format(day, 'd')}
                    </p>
                  </div>
                  {dayTotal > 0 && (
                    <span
                      className={`text-base font-bold ${isToday ? 'text-amber-700' : 'text-slate-900'}`}
                      data-testid={`day-total-${format(day, 'yyyy-MM-dd')}`}
                    >
                      {formatMinutesToDisplay(dayTotal)}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {dayEntries.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-2">No entries</p>
                ) : (
                  <div className="space-y-2">
                    {dayEntries.map(entry => (
                      <div 
                        key={entry.id} 
                        className="text-xs p-2 bg-white rounded border border-slate-200"
                      >
                        {entry.job_number && showJobNumbers && (
                          <p className="inline-block text-[10px] font-mono font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mb-1 break-all" title={entry.job_number}>
                            {formatJobBadge(entry)}
                          </p>
                        )}
                        <p className="font-semibold text-slate-900 leading-tight break-words text-sm">
                          {formatEntryLabel(entry, entry.job_title || 'Job')}
                          {entry.is_event && (
                            <span className="ml-1 inline-flex items-center text-[9px] font-bold tracking-wide bg-purple-100 text-purple-700 border border-purple-200 px-1 py-0.5 rounded align-middle">
                              EVENT
                            </span>
                          )}
                        </p>
                        {entry.client_name && (
                          <p className="text-[11px] text-slate-500 break-words mt-0.5">{entry.client_name}</p>
                        )}
                        {entry.contact_name && (
                          <p className="text-[10px] text-slate-500 italic break-words mt-0.5" title={entry.contact_name}>
                            Contact: {entry.contact_name}
                          </p>
                        )}
                        {/* Team-member bubbles — split the comma-separated
                            team_initials into a row of small circles to
                            visually match the avatar bubbles used in the
                            rest of the app (Layout header, Team page).
                            No per-member colour available on the entry
                            doc, so we use a uniform slate fill — color
                            could be added later if the entry response
                            ever carries assigned-user records. */}
                        {entry.team_initials && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {String(entry.team_initials).split(/[,\s]+/).filter(Boolean).map((ini, i) => {
                              const key = ini.toUpperCase();
                              const color = colorByInitials[key];
                              const isDefault = !color;
                              return (
                                <span
                                  key={`${ini}-${i}`}
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold font-mono shadow-sm border"
                                  title={ini}
                                  style={{
                                    backgroundColor: color || '#e2e8f0',
                                    color: isDefault ? '#334155' : getContrastTextColor(color),
                                    borderColor: isDefault ? '#cbd5e1' : color,
                                  }}
                                >
                                  {ini}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* Inline note display — saves the user a click into
                            the Edit dialog just to see what an entry was about. */}
                        <EntryNotesLine
                          entry={entry}
                          displayedLabel={formatEntryLabel(entry, entry.job_title || 'Job')}
                        />
                        {/* Team-Lead daily report — only renders if the
                            entry's owner is the team lead of this job. */}
                        <TeamLeadReportChip entry={entry} token={token} compact />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-bold text-slate-900">{formatMinutesToDisplay(getEntryMinutes(entry, 'total'))}</span>
                          <div className="flex items-center gap-4">
                            <button 
                              onClick={() => handleEditEntry(entry)}
                              className="p-1.5 -m-1.5 text-slate-400 hover:text-amber-500"
                              title="Edit"
                              data-testid={`edit-entry-${entry.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button 
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="p-1.5 -m-1.5 text-slate-400 hover:text-red-500"
                              title="Delete"
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <PayrollBox compact />
                      </div>
                    ))}
                    {/* Shop Prep daily total — bottom of the day's stack,
                        only when the day actually had any shop/prep time. */}
                    {shopMinutes > 0 && (
                      <div
                        className="mt-1 px-2 py-1 rounded bg-orange-50 border border-orange-200 flex items-center justify-between"
                        data-testid={`shop-prep-total-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className="text-[9px] uppercase tracking-widest font-semibold text-orange-700">Shop Prep Total</span>
                        <span className="text-[11px] font-bold text-orange-700">{formatMinutesToDisplay(shopMinutes)}</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Day-card category ribbon (JOB / TRAVEL / SHOP-OTHER)
                    removed per request — owners only need the day total
                    (rendered at the top of the card) for at-a-glance review. */}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-2xl font-['Barlow_Condensed'] tracking-tight text-slate-900">Time Entries</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'} this week · {formatMinutesToDisplay(totalMinutes)} total
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No time entries for this week</p>
            </div>
          ) : (() => {
            // Group entries by day. Sun → Sat (ascending) so admins read the
            // week like a normal calendar instead of reverse chronological.
            const groups = {};
            for (const e of entries) {
              const key = e.date;
              if (!groups[key]) groups[key] = [];
              groups[key].push(e);
            }
            const dates = Object.keys(groups).sort((a, b) => a.localeCompare(b));
            // Within each day, sort morning → end of day. Auto-tracked entries
            // have start_time; manual entries (no start_time) fall to the end.
            for (const k of dates) {
              groups[k].sort((a, b) =>
                (a.start_time || a.created_at || '9999').localeCompare(b.start_time || b.created_at || '9999')
              );
            }
            const isShopOther = (e) => {
              const jn = (e.job_number || '').toUpperCase();
              return jn.startsWith('OTHER') || jn.startsWith('SHOP-');
            };

            return (
              <div className="divide-y divide-slate-100">
                {dates.map(dateKey => {
                  const dayEntries = groups[dateKey];
                  const dayMinutes = dayEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                  const d = parseLocalDate(dateKey);
                  const isToday = format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  return (
                    <div key={dateKey} data-testid={`entries-day-${dateKey}`}>
                      {/* Sticky day-header */}
                      <div className={`flex items-baseline justify-between px-5 py-2.5 border-b border-slate-200 ${
                        isToday ? 'bg-amber-50' : 'bg-slate-50/70'
                      }`}>
                        <div className="flex items-baseline gap-3">
                          <p className={`text-xs uppercase tracking-wider font-bold ${isToday ? 'text-amber-700' : 'text-slate-500'}`}>
                            {format(d, 'EEEE')}
                          </p>
                          <p className={`text-lg font-bold font-['Barlow_Condensed'] leading-none ${isToday ? 'text-amber-700' : 'text-slate-900'}`}>
                            {format(d, 'MMM d')}
                          </p>
                          <span className="text-xs text-slate-400">
                            {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                          </span>
                        </div>
                        <span className={`text-sm font-bold ${isToday ? 'text-amber-700' : 'text-slate-700'}`}>
                          {formatMinutesToDisplay(dayMinutes)}
                        </span>
                      </div>

                      {/* Entry rows for this day */}
                      <div className="divide-y divide-slate-50">
                        {dayEntries.map(entry => {
                          const tTo = getEntryMinutes(entry, 'travel_to_job');
                          const tOn = getEntryMinutes(entry, 'time_on_job');
                          const tFrom = getEntryMinutes(entry, 'travel_from_job');
                          const total = getEntryMinutes(entry, 'total');
                          const shopMode = isShopOther(entry);
                          return (
                            <div
                              key={entry.id}
                              className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-amber-50/30 transition-colors"
                              data-testid={`entry-row-${entry.id}`}
                            >
                              {/* Job pill + title (5 cols) */}
                              <div className="col-span-12 md:col-span-5 min-w-0 flex items-center gap-2">
                                {entry.job_number && showJobNumbers ? (
                                  <span
                                    className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap max-w-[140px] truncate ${
                                      shopMode
                                        ? 'bg-slate-200 text-slate-700'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}
                                    title={entry.job_number}
                                  >
                                    {formatJobBadge(entry)}
                                  </span>
                                ) : (!showJobNumbers ? null : (
                                  <span className="text-xs text-slate-400">—</span>
                                ))}
                                {/* Source indicator — friction #18 */}
                                {entry.auto_source ? (
                                  <span
                                    className="inline-flex items-center text-emerald-600"
                                    title={`Auto-generated from ${entry.auto_source.replace(/_/g, ' ')}`}
                                    data-testid={`source-tracker-${entry.id}`}
                                  >
                                    <Clock className="w-3 h-3" />
                                  </span>
                                ) : (
                                  <span
                                    className="inline-flex items-center text-slate-400"
                                    title="Manual entry"
                                    data-testid={`source-manual-${entry.id}`}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </span>
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-900 text-sm truncate" title={entry.job_title || ''}>
                                    {formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : '—')}
                                  </p>
                                  {entry.client_name && !shopMode && (
                                    <p className="text-xs text-slate-500 truncate" title={entry.client_name}>
                                      {entry.client_name}
                                    </p>
                                  )}
                                  <EntryNotesLine
                                    entry={entry}
                                    displayedLabel={formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : '—')}
                                  />
                                </div>
                              </div>

                              {/* Team + Time flow (4 cols) */}
                              <div className="col-span-7 md:col-span-4 flex items-center gap-3 text-xs">
                                {entry.team_initials && (
                                  <span className="font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px]">
                                    {entry.team_initials}
                                  </span>
                                )}
                                <div className="flex items-center gap-1.5 text-slate-600 flex-wrap">
                                  {tTo > 0 && (
                                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded" title="Travel To">
                                      <span className="text-[10px] uppercase">→</span>
                                      {formatMinutesToDisplay(tTo)}
                                    </span>
                                  )}
                                  {tOn > 0 && (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                      shopMode ? 'bg-slate-100 text-slate-700' : 'bg-emerald-50 text-emerald-700'
                                    }`} title={shopMode ? 'Shop / Other' : 'On Job'}>
                                      {formatMinutesToDisplay(tOn)}
                                    </span>
                                  )}
                                  {tFrom > 0 && (
                                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded" title="Travel From">
                                      <span className="text-[10px] uppercase">←</span>
                                      {formatMinutesToDisplay(tFrom)}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Total + Notes + Actions (3 cols) */}
                              <div className="col-span-5 md:col-span-3 flex items-center justify-end gap-3">
                                {entry.notes && !shopMode && (
                                  <span
                                    className="hidden lg:inline text-xs text-slate-500 italic truncate max-w-[140px]"
                                    title={entry.notes}
                                  >
                                    {entry.notes}
                                  </span>
                                )}
                                <span className="font-bold text-slate-900 text-base whitespace-nowrap" title="Total">
                                  {formatMinutesToDisplay(total)}
                                </span>
                                <div className="flex items-center gap-2">
                                  {entry.is_locked && (
                                    <span
                                      className="inline-flex items-center text-slate-400"
                                      title={`Locked — week is ${entry.locking_week_status}. Reset to edit.`}
                                      data-testid={`locked-${entry.id}`}
                                    >
                                      <Lock className="w-3.5 h-3.5" />
                                    </span>
                                  )}
                                  {Array.isArray(entry.audit_trail) && entry.audit_trail.length > 0 && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-emerald-500 hover:text-emerald-600"
                                          title={`Edited ${entry.audit_trail.length}×`}
                                          data-testid={`audit-${entry.id}`}
                                        >
                                          <Shield className="w-3.5 h-3.5" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80 p-3" align="end">
                                        <p className="text-xs font-semibold text-slate-700 mb-2">
                                          Change history ({entry.audit_trail.length})
                                        </p>
                                        <div className="max-h-64 overflow-y-auto space-y-2">
                                          {entry.audit_trail.slice().reverse().map((a, i) => (
                                            <div key={i} className="text-xs border-l-2 border-emerald-200 pl-2">
                                              <p className="font-medium text-slate-700">
                                                {a.by_name || 'someone'} changed <span className="font-mono">{a.field}</span>
                                              </p>
                                              <p className="text-slate-500">
                                                <span className="line-through opacity-70">{String(a.from ?? '—')}</span>
                                                <span className="mx-1">→</span>
                                                <span className="font-semibold text-emerald-700">{String(a.to ?? '—')}</span>
                                              </p>
                                              <p className="text-[10px] text-slate-400 mt-0.5">
                                                {a.at ? new Date(a.at).toLocaleString() : ''} · {a.source}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                  {!entry.is_locked && <ReassignPopover entry={entry} />}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600 disabled:opacity-40 disabled:hover:text-slate-400"
                                    onClick={() => handleEditEntry(entry)}
                                    disabled={entry.is_locked && !isManager}
                                    title={entry.is_locked ? `Locked (${entry.locking_week_status})` : 'Edit'}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-slate-300 hover:text-red-500 disabled:opacity-40 disabled:hover:text-slate-300"
                                    onClick={() => handleDeleteEntry(entry.id)}
                                    disabled={entry.is_locked && !isManager}
                                    title={entry.is_locked ? `Locked (${entry.locking_week_status})` : 'Delete'}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </>
    );
  };

  // Spreadsheet-style weekly view: Project | Task | Sun..Sat | Total
  const renderMyWeekSpreadsheet = () => {
    const dayStart = startOfWeek(selectedWeek, { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: dayStart, end: endOfWeek(selectedWeek, { weekStartsOn: 0 }) });

    // Pivot entries -> rows keyed by (job_id || job_number || job_title)
    const rowMap = {};
    entries.forEach(e => {
      const d = parseLocalDate(e.date);
      const di = days.findIndex(dd => isSameDay(dd, d));
      if (di < 0) return;
      const key = e.job_id || e.job_number || `__custom__${e.job_title || e.notes || 'Other'}`;
      if (!rowMap[key]) {
        rowMap[key] = {
          project: e.job_title || e.custom_job_description || 'Other',
          job_number: e.job_number || '',
          task: e.notes || '',
          daily: [0, 0, 0, 0, 0, 0, 0],
          dailyEntries: [[], [], [], [], [], [], []],
          total: 0,
        };
      }
      const mins = getEntryMinutes(e, 'total');
      rowMap[key].daily[di] += mins;
      rowMap[key].dailyEntries[di].push(e);
      rowMap[key].total += mins;
      if (!rowMap[key].task && e.notes) rowMap[key].task = e.notes;
    });
    const rows = Object.values(rowMap).sort((a, b) => b.total - a.total);
    const dayTotals = [0, 0, 0, 0, 0, 0, 0];
    rows.forEach(r => r.daily.forEach((m, i) => { dayTotals[i] += m; }));
    const grandTotal = dayTotals.reduce((s, m) => s + m, 0);

    return (
      <Card className="border-slate-200" data-testid="my-week-spreadsheet">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Barlow_Condensed']">
            Week of {format(dayStart, 'MMM d, yyyy')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No time entries for this week.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-700 hover:bg-slate-700">
                    <TableHead className="text-white font-bold min-w-[180px]">Project</TableHead>
                    <TableHead className="text-white font-bold min-w-[160px]">Task / Notes</TableHead>
                    {days.map((d, i) => (
                      <TableHead key={i} className="text-white font-bold text-center min-w-[64px]">
                        <div className="leading-tight">{format(d, 'EEE')}</div>
                        <div className="text-[10px] text-slate-300 font-normal">{format(d, 'M/d')}</div>
                      </TableHead>
                    ))}
                    <TableHead className="text-white font-bold text-right min-w-[88px] bg-slate-800">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, ri) => (
                    <TableRow key={ri}>
                      <TableCell className="align-top">
                        <div className="flex items-start gap-2 flex-wrap min-w-0">
                          {r.job_number && (
                            <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap flex-shrink-0" title={r.job_number}>
                              {formatJobBadge(r)}
                            </span>
                          )}
                          <span className="text-sm text-slate-800 whitespace-normal break-words leading-snug">
                            {titleCaseLabel(r.project)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[220px] whitespace-normal break-words leading-snug align-top" title={r.task}>
                        {r.task || '—'}
                      </TableCell>
                      {r.daily.map((mins, di) => {
                        const tooltip = (r.dailyEntries[di] || []).map(e => {
                          const parts = [];
                          if (e.start_time && e.end_time) {
                            try { parts.push(`${format(new Date(e.start_time), 'h:mma')} → ${format(new Date(e.end_time), 'h:mma')}`); } catch {}
                          }
                          const tt = getEntryMinutes(e, 'travel_to_job'), tj = getEntryMinutes(e, 'time_on_job'), tf = getEntryMinutes(e, 'travel_from_job');
                          if (tt) parts.push(`Travel to: ${formatMinutesToDisplay(tt)}`);
                          if (tj) parts.push(`On job: ${formatMinutesToDisplay(tj)}`);
                          if (tf) parts.push(`Travel from: ${formatMinutesToDisplay(tf)}`);
                          if (e.notes) parts.push(e.notes);
                          return parts.join(' • ');
                        }).join('\n');
                        return (
                          <TableCell key={di} className="text-center text-sm" title={tooltip}>
                            {mins > 0 ? <span className="text-slate-900 font-medium">{formatMinutesToDisplay(mins)}</span> : <span className="text-slate-300">—</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-semibold text-slate-900 bg-slate-50">
                        {formatMinutesToDisplay(r.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <TableCell className="font-bold text-slate-700">Daily Total</TableCell>
                    <TableCell />
                    {dayTotals.map((m, i) => (
                      <TableCell key={i} className="text-center font-bold text-slate-900">
                        {m > 0 ? formatMinutesToDisplay(m) : <span className="text-slate-400">—</span>}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-bold text-amber-600 text-base">
                      {formatMinutesToDisplay(grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <p className="text-[11px] text-slate-400 mt-3 italic">
                Read-only roll-up. Hover any cell for start/end times and travel/on-job breakdown. Edit time entries from Time Tracker or the Day View.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="h-64 bg-slate-200 animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
          Timesheet
        </h1>
        {(activeTab === 'my-timesheet' || (activeTab === 'team-timesheets' && memberFocusMode)) && (
          <div className="flex items-center gap-3 flex-wrap">
            <label
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-xs text-slate-700 cursor-pointer select-none"
              data-testid="timesheet-show-job-numbers-label"
              title="Toggles the J20260xxx-### pills on the calendar cards and the Time Entries list. Off by default for payroll review; your choice is remembered."
            >
              <input
                type="checkbox"
                checked={showJobNumbers}
                onChange={(e) => setShowJobNumbers(e.target.checked)}
                className="accent-orange-600"
                data-testid="timesheet-show-job-numbers-toggle"
              />
              Show Job Numbers
            </label>
            {activeTab === 'my-timesheet' && (<>
            <div className="inline-flex h-9 border border-slate-200 rounded-md overflow-hidden bg-white">
              <button
                type="button"
                onClick={() => setMyTimesheetViewMode('day')}
                className={`px-3 h-full text-xs flex items-center gap-1 ${myTimesheetViewMode === 'day' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="my-view-day"
              >
                Day View
              </button>
              <button
                type="button"
                onClick={() => setMyTimesheetViewMode('week')}
                className={`px-3 h-full text-xs flex items-center gap-1 border-l border-slate-200 ${myTimesheetViewMode === 'week' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="my-view-week"
              >
                Week View
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            {weeklyTimesheet?.id && (
              <Button
                size="sm"
                onClick={() => navigate(`/timesheet/${weeklyTimesheet.id}/preview`)}
                data-testid="print-preview-btn"
                className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print / Save PDF
              </Button>
            )}
            <Button
              size="sm"
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={() => setShowAddDialog(true)}
              data-testid="add-time-entry-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
            </>)}
          </div>
        )}
      </div>

      {/* Tabs for manager view */}
      {isManager ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="my-timesheet" className="data-[state=active]:bg-white" data-testid="tab-my">
              <User className="w-4 h-4 mr-2" />
              My Timesheet
            </TabsTrigger>
            <TabsTrigger
              value="team-timesheets"
              className="data-[state=active]:bg-white"
              data-testid="tab-team"
              onClick={() => {
                // Re-clicking the already-active "Team Timesheets" tab while
                // viewing a member (focus mode) does nothing by default —
                // Radix only fires onValueChange when the value changes.
                // Users expect the tab to act like a back-to-grid affordance,
                // so mirror the "Back to team list" reset here.
                if (memberFocusMode) {
                  setMemberFocusMode(false);
                  setSelectedMember(null);
                  setSelectedMemberWeekly(null);
                  setInvoiceNumber('');
                }
              }}
            >
              <Users className="w-4 h-4 mr-2" />
              Team Timesheets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my-timesheet" className="space-y-6">
            {/* Week Navigation - Consistent with Expense Reports */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                {/* Navigation Row */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-center">
                    <Button variant="outline" className="mr-4" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <div className="text-center px-4">
                      <h2 className="font-bold text-slate-900 text-lg">
                        Week {getWeek(weekStart)}, {weekStart.getFullYear()}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                      </p>
                    </div>
                    <Button variant="outline" className="ml-4" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {/* Mini month widget — click any day to jump to its week.
                      Independent month cursor so browsing doesn't reload data. */}
                  <MiniMonth
                    cursorMonth={miniMonthCursor}
                    selectedWeek={selectedWeek}
                    onPrevMonth={() => setMiniMonthCursor(subMonths(miniMonthCursor, 1))}
                    onNextMonth={() => setMiniMonthCursor(addMonths(miniMonthCursor, 1))}
                    onSelectDay={(d) => {
                      setSelectedWeek(d);
                      setMiniMonthCursor(d);
                    }}
                    onJumpToToday={() => {
                      const today = new Date();
                      setSelectedWeek(today);
                      setMiniMonthCursor(today);
                    }}
                  />

                  {/* View/Edit Button - Like Expense Reports */}
                  <Button 
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold mx-auto"
                    onClick={() => setShowDetailDialog(true)}
                    data-testid="view-edit-timesheet-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    {entries.length > 0 ? 'View / Edit This Week\'s Timesheet' : 'Add Time Entries'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* My Reports List - Consistent with Expense Reports */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-slate-900">My Timesheets</h3>
              </div>
              
              {/* Current Week Summary Card — owner-grade header
                  Big total, status bar on the left edge, week meta */}
              {(() => {
                const status = weeklyTimesheet?.status
                  || (entries.length > 0 ? 'Draft' : null);
                const isReturned = weeklyTimesheet?.status === 'Draft' && weeklyTimesheet?.rejection_reason;
                const stripeColor = isReturned
                  ? 'bg-amber-500'
                  : status === 'Approved'
                    ? 'bg-emerald-500'
                    : status === 'Paid'
                      ? 'bg-slate-500'
                      : status === 'Submitted'
                        ? 'bg-blue-500'
                        : status === 'Draft'
                          ? 'bg-red-500'
                          : 'bg-slate-200';
                const statusLabel = isReturned ? 'Returned' : status || '—';
                return (
                  <Card
                    className={`relative border-slate-200 hover:border-amber-300 transition-colors cursor-pointer overflow-hidden`}
                    onClick={() => setShowDetailDialog(true)}
                    data-testid="my-week-summary"
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripeColor}`} />
                    <CardContent className="p-4 pl-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                            Week {getWeek(weekStart)} · {weekStart.getFullYear()}
                          </p>
                          <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-tight">
                            {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                          </h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge className={`text-xs ${
                              isReturned ? 'bg-amber-100 text-amber-700'
                              : status === 'Approved' ? 'bg-emerald-100 text-emerald-700'
                              : status === 'Paid' ? 'bg-slate-200 text-slate-700'
                              : status === 'Submitted' ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-50 text-red-700'
                            }`}>{statusLabel}</Badge>
                            <span className="text-xs text-slate-500">{entries.length} entries</span>
                            {weeklyTimesheet?.timesheet_number && (
                              <span className="text-xs text-slate-400 font-mono">{weeklyTimesheet.timesheet_number}</span>
                            )}
                          </div>
                          {isReturned && (
                            <p className="text-xs text-amber-600 mt-2">⚠ Needs correction: {weeklyTimesheet.rejection_reason}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-none">
                            {formatMinutesToDisplay(totalMinutes)}
                          </p>
                          <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">Week Total</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Inline Day / Week view — controlled by the header toggle */}
              <div data-testid="my-timesheet-inline-view">
                {renderMyTimesheetContent()}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="team-timesheets" className="space-y-6">
            {/* Week Navigation for Team */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">
                        Week {getWeek(weekStart)}
                      </span>
                    </div>
                    <h2 className="font-semibold text-slate-900">
                      {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                    </h2>
                    <p className="text-sm text-slate-500">Team Total: {formatMinutesToDisplay(teamTotalMinutes)}</p>
                  </div>
                  <Button variant="ghost" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
                {/* Export Buttons — wrap on mobile, full labels on desktop */}
                <div className="flex flex-wrap justify-center gap-2 mt-4 pt-4 border-t border-slate-200">
                  <Button variant="outline" size="sm" onClick={handleExportTeamCSV} data-testid="export-team-csv-btn" className="h-9">
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Export CSV</span>
                    <span className="sm:hidden ml-1">CSV</span>
                  </Button>
                  <Button
                    className="bg-amber-500 hover:bg-amber-600 text-white h-9"
                    size="sm"
                    onClick={memberFocusMode ? handlePrintMemberView : handlePrintTeamView}
                    data-testid="export-team-pdf-btn"
                    title={memberFocusMode ? "Download this employee's timesheet PDF" : "Internal use only - opens browser print dialog"}
                  >
                    <Printer className="w-4 h-4 sm:mr-2" />
                    PDF
                  </Button>
                  {isManager && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.assign('/payroll-reconciliation')}
                      data-testid="open-payroll-recon-btn"
                      title="Compare Live Status vs Timesheet for every worker — review before approving payroll"
                      className="h-9"
                    >
                      <ShieldCheck className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Reconciliation</span>
                      <span className="sm:hidden ml-1">Recon</span>
                    </Button>
                  )}
                  {isManager && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/timesheet/wall')}
                      data-testid="open-wall-view-btn"
                      title="Full-screen crew dashboard — designed for an office TV/monitor"
                      className="h-9"
                    >
                      <Maximize2 className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Wall View</span>
                      <span className="sm:hidden ml-1">Wall</span>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Focus-mode banner — shown when arriving from Approval/Wall/Office deep-link.
                Hides the employee grid so the approver lands directly on the
                one member's entries that need attention. Shows the workflow envelope
                (status, submitted_at, approved_by) so admins see the same info the
                old Office Dashboard modal showed. */}
            {memberFocusMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-3" data-testid="focus-mode-banner">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-slate-700">
                    Viewing <span className="font-semibold">{selectedMember?.user_name || '...'}</span>'s week — approval focus mode.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => { setMemberFocusMode(false); setSelectedMember(null); setSelectedMemberWeekly(null); setInvoiceNumber(''); }}
                    data-testid="focus-mode-exit"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back to team list
                  </Button>
                </div>
                {/* Workflow envelope strip */}
                {selectedMemberWeekly ? (() => {
                  const status = selectedMemberWeekly.status || 'Draft';
                  const statusTone = {
                    Draft:     'bg-slate-200 text-slate-700',
                    Submitted: 'bg-blue-100 text-blue-700',
                    Approved:  'bg-emerald-100 text-emerald-700',
                    Paid:      'bg-purple-100 text-purple-700',
                  }[status] || 'bg-slate-200 text-slate-700';
                  const subAt = selectedMemberWeekly.submitted_at
                    ? format(new Date(selectedMemberWeekly.submitted_at), 'MMM d, h:mm a')
                    : '—';
                  const apprBy = selectedMemberWeekly.approved_by_name || selectedMemberWeekly.approved_by || '—';
                  // Derive total + count from the entries we just loaded so
                  // the strip can never disagree with the entries list below.
                  // The envelope's cached total_hours can drift after edits;
                  // the live sum is the truth that payroll exports use.
                  const liveTotalMinutes = teamEntries.reduce((s, e) => s + (getEntryMinutes(e, 'total') || 0), 0);
                  const totalHours = (liveTotalMinutes / 60).toFixed(2);
                  const cachedTotal = (selectedMemberWeekly.total_hours || 0);
                  const driftWarn = Math.abs((liveTotalMinutes / 60) - cachedTotal) > 0.05;
                  return (
                    <div
                      className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-amber-200"
                      data-testid="focus-mode-workflow-strip"
                    >
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                        <span className={`px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${statusTone}`} data-testid="focus-status-pill">
                          {status}
                        </span>
                        <span><strong className="text-slate-900">{totalHours}h</strong> · {teamEntries.length} entries</span>
                        {driftWarn && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-semibold"
                            title={`Envelope cached total is ${cachedTotal.toFixed(2)}h but the entries sum to ${totalHours}h. Save any pending edits, then click 'Recompute' on the envelope.`}
                            data-testid="focus-drift-warning"
                          >
                            ⚠ Drift {cachedTotal.toFixed(2)}h
                          </span>
                        )}
                        <span>Submitted <strong className="text-slate-900">{subAt}</strong></span>
                        <span>Approved by <strong className="text-slate-900">{apprBy}</strong></span>
                        {selectedMemberWeekly.timesheet_number && (
                          <span className="font-mono text-[11px] text-slate-400">{selectedMemberWeekly.timesheet_number}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {status === 'Submitted' && (
                          <>
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={handleFocusApprove} data-testid="focus-approve-btn">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={handleFocusReject} data-testid="focus-reject-btn">
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Send back
                            </Button>
                          </>
                        )}
                        {status === 'Approved' && (
                          <>
                            <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-500 text-white" onClick={handleFocusMarkPaid} data-testid="focus-mark-paid-btn">
                              <DollarSign className="w-3.5 h-3.5 mr-1" /> Mark Paid
                            </Button>
                            <Button size="sm" variant="outline" className="h-8" onClick={handleFocusReopen} data-testid="focus-reopen-btn">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                            </Button>
                          </>
                        )}
                        {status === 'Paid' && (
                          <Button size="sm" variant="outline" className="h-8" onClick={handleFocusReopen} data-testid="focus-reopen-btn">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })() : (
                  <div className="pt-2 border-t border-amber-200 text-xs text-slate-500" data-testid="focus-mode-no-weekly">
                    No weekly envelope for this week — employee has not submitted yet (entries shown are still editable).
                  </div>
                )}
              </div>
            )}

            {/* Team Summary Cards — hidden while focused on a single member */}
            {!memberFocusMode && (
            <>
            {/* Admin-only toggle to surface deactivated employees in the
                team grid (e.g. when auditing a past week where an inactive
                employee still had entries). Hidden by default to keep the
                live roster clean. */}
            {isManager && (
              <div className="flex items-center justify-end -mt-2">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="team-show-inactive-toggle-label">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-amber-500"
                    checked={showInactiveMembers}
                    onChange={(e) => setShowInactiveMembers(e.target.checked)}
                    data-testid="team-show-inactive-toggle"
                  />
                  Show inactive employees
                </label>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamSummary.map(member => (
                <Card 
                  key={member.user_id} 
                  className={`border-slate-200 hover:border-amber-500/50 transition-colors ${
                    selectedMember?.user_id === member.user_id ? 'ring-2 ring-amber-500' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div 
                      className="flex items-start gap-3 cursor-pointer"
                      onClick={() => {
                        const ws = format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd');
                        navigate(`/timesheet?user=${member.user_id}&week=${ws}`);
                      }}
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-sm"
                        style={{ backgroundColor: member.calendar_color || '#f59e0b', color: getContrastTextColor(member.calendar_color || '#f59e0b') }}
                      >
                        {member.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{member.user_name}</h3>
                        <p className="text-xs text-slate-500 truncate">{member.user_email}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Badge variant="outline" className="text-xs capitalize">{member.role}</Badge>
                          {member.active === false && (
                            <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300" data-testid={`team-card-inactive-${member.user_id}`}>
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-600">{formatMinutesToDisplay(member.total_minutes ?? hoursToMinutes(member.total_hours))}</p>
                        <p className="text-xs text-slate-500">total</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100">
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-900">{formatMinutesToDisplay(member.travel_minutes ?? hoursToMinutes(member.travel_hours))}</p>
                        <p className="text-xs text-slate-500">Travel</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-900">{formatMinutesToDisplay(member.on_job_minutes ?? hoursToMinutes(member.on_job_hours))}</p>
                        <p className="text-xs text-slate-500">On Job</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-900">{member.entry_count}</p>
                        <p className="text-xs text-slate-500">Entries</p>
                      </div>
                    </div>
                    {/* Action buttons — View opens the full editable focus-mode
                        page (same view the Office Dashboard and Approval Dashboard
                        use), Add Entry stays as a fast path for quick logging. */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          const ws = format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd');
                          navigate(`/timesheet?user=${member.user_id}&week=${ws}`);
                        }}
                        data-testid={`team-card-view-${member.user_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View &amp; Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddEntryForMember(member);
                        }}
                        data-testid={`team-card-add-${member.user_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Entry
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            </>
            )}

            {/* Selected Member Detail */}
            {selectedMember && (
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm"
                      style={{ backgroundColor: selectedMember.calendar_color || '#f59e0b', color: getContrastTextColor(selectedMember.calendar_color || '#f59e0b') }}
                    >
                      {selectedMember.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    {selectedMember.user_name}'s Time Entries
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* Payroll Invoice # input removed 2026-05-27 — the
                        company now writes the invoice # directly into
                        the per-entry dashed "Payroll" box on each day
                        card, so the global toolbar field was duplicating
                        a workflow that's already handled inline. The
                        `invoiceNumber` state + `?invoice=` query-string
                        plumbing into the PDF route stays in place so
                        any future per-week QB # field can be reintroduced
                        without re-wiring the preview URL. */}
                    <div className="inline-flex h-9 border border-slate-200 rounded-md overflow-hidden bg-white">
                      <button
                        type="button"
                        onClick={() => setMemberViewMode('calendar')}
                        className={`px-3 h-full text-xs flex items-center gap-1 ${memberViewMode === 'calendar' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                        data-testid="member-view-calendar"
                      >
                        Calendar
                      </button>
                      <button
                        type="button"
                        onClick={() => setMemberViewMode('grid')}
                        className={`px-3 h-full text-xs flex items-center gap-1 border-l border-slate-200 ${memberViewMode === 'grid' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                        data-testid="member-view-grid"
                      >
                        Week Grid
                      </button>
                      <button
                        type="button"
                        onClick={() => setMemberViewMode('table')}
                        className={`px-3 h-full text-xs flex items-center gap-1 border-l border-slate-200 ${memberViewMode === 'table' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                        data-testid="member-view-table"
                      >
                        List
                      </button>
                    </div>
                    <Button 
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                      onClick={() => handleAddEntryForMember(selectedMember)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Entry
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedMember(null); setMemberFocusMode(false); setSelectedMemberWeekly(null); setInvoiceNumber(''); }}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {teamEntries.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No time entries for this week</p>
                      <Button 
                        className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900"
                        onClick={() => handleAddEntryForMember(selectedMember)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Entry
                      </Button>
                    </div>
                  ) : memberViewMode === 'calendar' ? (
                    // ── CALENDAR: Sun → Sat day cards (like My Timesheet Day View) ──
                    (() => {
                      const calStart = startOfWeek(selectedWeek, { weekStartsOn: 0 });
                      const calEnd = endOfWeek(selectedWeek, { weekStartsOn: 0 });
                      const days = eachDayOfInterval({ start: calStart, end: calEnd });
                      const isShopOther = (e) => {
                        const jn = (e.job_number || '').toUpperCase();
                        return jn.startsWith('OTHER') || jn.startsWith('SHOP-');
                      };
                      const weekStatus = selectedMemberWeekly?.status;
                      const isLocked = weekStatus === 'Approved' || weekStatus === 'Paid';
                      // Group entries by date string and sort within each day
                      // morning → end of day so each card reads top-down.
                      const byDate = {};
                      teamEntries.forEach(e => {
                        const k = e.date;
                        if (!byDate[k]) byDate[k] = [];
                        byDate[k].push(e);
                      });
                      Object.keys(byDate).forEach(k => {
                        byDate[k].sort((a, b) =>
                          (a.start_time || a.created_at || '9999').localeCompare(b.start_time || b.created_at || '9999')
                        );
                      });
                      const weekTotalMin = teamEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                      return (
                        <div data-testid="member-calendar-view">
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
                            {days.map(day => {
                              const key = format(day, 'yyyy-MM-dd');
                              const dayEntries = byDate[key] || [];
                              const dayTotal = dayEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                              const isToday = key === format(new Date(), 'yyyy-MM-dd');
                              return (
                                <Card
                                  key={key}
                                  className={`border-slate-200 ${isToday ? 'border-amber-500 bg-amber-50/40' : ''}`}
                                  data-testid={`member-calendar-day-${key}`}
                                >
                                  <CardHeader className="p-3 pb-2">
                                    <div className="flex items-baseline justify-between gap-2">
                                      <div>
                                        <p className={`text-xs uppercase tracking-wide font-semibold ${isToday ? 'text-amber-600' : 'text-slate-500'}`}>
                                          {format(day, 'EEE')}
                                        </p>
                                        <p className={`text-2xl font-bold font-['Barlow_Condensed'] leading-none ${isToday ? 'text-amber-600' : 'text-slate-900'}`}>
                                          {format(day, 'd')}
                                        </p>
                                      </div>
                                      {dayTotal > 0 && (
                                        <span
                                          className={`text-sm font-bold ${isToday ? 'text-amber-700' : 'text-slate-900'}`}
                                          data-testid={`member-calendar-day-total-${key}`}
                                        >
                                          {formatMinutesToDisplay(dayTotal)}
                                        </span>
                                      )}
                                    </div>
                                  </CardHeader>
                                  <CardContent className="p-3 pt-0">
                                    {dayEntries.length === 0 ? (
                                      <p className="text-xs text-slate-400 text-center py-2">No entries</p>
                                    ) : (
                                      <div className="space-y-2">
                                        {dayEntries.map(entry => {
                                          const total = getEntryMinutes(entry, 'total');
                                          const shopMode = isShopOther(entry);
                                          return (
                                            <div
                                              key={entry.id}
                                              className="text-xs p-2 bg-white rounded border border-slate-200"
                                              data-testid={`member-calendar-entry-${entry.id}`}
                                            >
                                              {entry.job_number && showJobNumbers && (
                                                <p
                                                  className={`inline-block text-[10px] font-mono font-bold px-1.5 py-0.5 rounded mb-1 break-all ${shopMode ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}
                                                  title={entry.job_number}
                                                >
                                                  {formatJobBadge(entry)}
                                                </p>
                                              )}
                                              <p className="font-semibold text-slate-900 leading-tight break-words text-sm" title={entry.job_title || ''}>
                                                {formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : (entry.job_title || 'Job'))}
                                                {entry.is_event && (
                                                  <span className="ml-1 inline-flex items-center text-[9px] font-bold tracking-wide bg-purple-100 text-purple-700 border border-purple-200 px-1 py-0.5 rounded align-middle">
                                                    EVENT
                                                  </span>
                                                )}
                                              </p>
                                              {entry.client_name && !shopMode && (
                                                <p className="text-[11px] text-slate-500 break-words mt-0.5">{entry.client_name}</p>
                                              )}
                                              {entry.contact_name && !shopMode && (
                                                <p className="text-[10px] text-slate-500 italic break-words mt-0.5" title={entry.contact_name}>
                                                  Contact: {entry.contact_name}
                                                </p>
                                              )}
                                              <EntryNotesLine
                                                entry={entry}
                                                displayedLabel={formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : (entry.job_title || 'Job'))}
                                              />
                                              {/* Team-member bubbles — mirror the
                                                  "My Timesheet" Day View: split
                                                  team_initials into a row of
                                                  small circles, coloured per
                                                  member from teamMembers'
                                                  calendar_color so admin
                                                  review matches Calendar/PDF. */}
                                              {entry.team_initials && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                  {String(entry.team_initials).split(/[,\s]+/).filter(Boolean).map((ini, i) => {
                                                    const key = ini.toUpperCase();
                                                    const color = colorByInitials[key];
                                                    const isDefault = !color;
                                                    return (
                                                      <span
                                                        key={`${ini}-${i}`}
                                                        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold font-mono shadow-sm border"
                                                        title={ini}
                                                        style={{
                                                          backgroundColor: color || '#e2e8f0',
                                                          color: isDefault ? '#334155' : getContrastTextColor(color),
                                                          borderColor: isDefault ? '#cbd5e1' : color,
                                                        }}
                                                      >
                                                        {ini}
                                                      </span>
                                                    );
                                                  })}
                                                </div>
                                              )}
                                              <div className="flex items-center justify-between mt-2">
                                                <span className="text-sm font-bold text-slate-900">{formatMinutesToDisplay(total)}</span>
                                                <div className="flex items-center gap-4">
                                                  {isLocked && (
                                                    <Lock
                                                      className="w-3 h-3 text-emerald-600"
                                                      aria-label="Locked — week is approved or paid"
                                                      data-testid={`member-calendar-lock-${entry.id}`}
                                                    />
                                                  )}
                                                  <button
                                                    onClick={() => handleEditEntry(entry)}
                                                    disabled={isLocked}
                                                    className="p-1.5 -m-1.5 text-slate-400 hover:text-amber-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    title={isLocked ? `Locked — ${weekStatus}` : 'Edit'}
                                                    data-testid={`member-calendar-edit-${entry.id}`}
                                                  >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteEntry(entry.id, entry.user_id)}
                                                    disabled={isLocked}
                                                    className="p-1.5 -m-1.5 text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    title={isLocked ? `Locked — ${weekStatus}` : 'Delete'}
                                                    data-testid={`member-calendar-delete-${entry.id}`}
                                                  >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                  </button>
                                                </div>
                                              </div>
                                              <PayrollBox compact />
                                            </div>
                                          );
                                        })}
                                        {/* Shop Prep daily total — mirror
                                            My Timesheet: only render when the
                                            day has any shop/prep time. */}
                                        {(() => {
                                          const shopMinutes = dayEntries.reduce(
                                            (s, e) => (isShopEntry(e) ? s + getEntryMinutes(e, 'total') : s),
                                            0,
                                          );
                                          if (shopMinutes <= 0) return null;
                                          return (
                                            <div
                                              className="mt-1 px-2 py-1 rounded bg-orange-50 border border-orange-200 flex items-center justify-between"
                                              data-testid={`member-shop-prep-total-${key}`}
                                            >
                                              <span className="text-[9px] uppercase tracking-widest font-semibold text-orange-700">Shop Prep Total</span>
                                              <span className="text-[11px] font-bold text-orange-700">{formatMinutesToDisplay(shopMinutes)}</span>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                    {/* Category breakdown ribbon removed
                                        per request — only the day total at
                                        the top of the card is needed. */}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-between px-5 py-3 mt-4 bg-slate-100 border-t border-slate-200 rounded">
                            <span className="text-sm font-semibold text-slate-700">Week Total</span>
                            <span className="text-lg font-bold text-amber-600 font-['Barlow_Condensed']">
                              {formatMinutesToDisplay(weekTotalMin)}
                            </span>
                          </div>

                          {/* Time Entries grouped list — mirrors the worker's
                              own "Time Entries" card so admins reviewing a
                              team member's week see the exact same layout.
                              Days ascending Sun → Sat; entries within a day
                              sorted morning → end of day. */}
                          {teamEntries.length > 0 && (() => {
                            const isShopOther = (e) => {
                              const jn = (e.job_number || '').toUpperCase();
                              return jn.startsWith('OTHER') || jn.startsWith('SHOP-');
                            };
                            const groups = {};
                            for (const e of teamEntries) {
                              const key = e.date;
                              if (!groups[key]) groups[key] = [];
                              groups[key].push(e);
                            }
                            const dates = Object.keys(groups).sort((a, b) => a.localeCompare(b));
                            for (const k of dates) {
                              groups[k].sort((a, b) =>
                                (a.start_time || a.created_at || '9999').localeCompare(b.start_time || b.created_at || '9999')
                              );
                            }
                            return (
                              <Card className="border-slate-200 mt-4" data-testid="member-calendar-time-entries">
                                <CardHeader className="pb-3">
                                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <div>
                                      <CardTitle className="text-2xl font-['Barlow_Condensed'] tracking-tight text-slate-900">
                                        Time Entries
                                      </CardTitle>
                                      <p className="text-xs text-slate-500 mt-0.5">
                                        {teamEntries.length} {teamEntries.length === 1 ? 'entry' : 'entries'} this week · {formatMinutesToDisplay(weekTotalMin)} total
                                      </p>
                                    </div>
                                  </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                  <div className="divide-y divide-slate-100">
                                    {dates.map(dateKey => {
                                      const dayEntries = groups[dateKey];
                                      const dayDate = parseISO(dateKey);
                                      const dayTotal = dayEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                                      return (
                                        <div key={dateKey}>
                                          <div className="bg-slate-50 px-4 py-2 flex items-baseline justify-between gap-2 flex-wrap border-b border-slate-100" data-testid={`team-entries-day-${dateKey}`}>
                                            <div className="flex items-baseline gap-2 min-w-0">
                                              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                                                {format(dayDate, 'EEEE')}
                                              </span>
                                              <span className="text-lg font-bold text-slate-900 font-['Barlow_Condensed']">
                                                {format(dayDate, 'MMM d')}
                                              </span>
                                              <span className="text-xs text-slate-500">
                                                {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                                              </span>
                                            </div>
                                            <span className="text-sm font-bold text-slate-700">
                                              {formatMinutesToDisplay(dayTotal)}
                                            </span>
                                          </div>
                                          <div>
                                            {dayEntries.map(entry => {
                                              const tTo = getEntryMinutes(entry, 'travel_to_job');
                                              const tOn = getEntryMinutes(entry, 'time_on_job');
                                              const tFrom = getEntryMinutes(entry, 'travel_from_job');
                                              const total = getEntryMinutes(entry, 'total');
                                              const shopMode = isShopOther(entry);
                                              return (
                                                <div
                                                  key={entry.id}
                                                  className="grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-amber-50/30 transition-colors"
                                                  data-testid={`team-entry-row-${entry.id}`}
                                                >
                                                  {/* Job pill + title (5 cols) — mirrors My Timesheet */}
                                                  <div className="col-span-12 md:col-span-5 min-w-0 flex items-center gap-2">
                                                    {entry.job_number && showJobNumbers ? (
                                                      <span
                                                        className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap max-w-[140px] truncate ${
                                                          shopMode ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-700'
                                                        }`}
                                                        title={entry.job_number}
                                                      >
                                                        {formatJobBadge(entry)}
                                                      </span>
                                                    ) : null}
                                                    {entry.auto_source ? (
                                                      <span
                                                        className="inline-flex items-center text-emerald-600"
                                                        title={`Auto-generated from ${entry.auto_source.replace(/_/g, ' ')}`}
                                                        data-testid={`team-source-tracker-${entry.id}`}
                                                      >
                                                        <Clock className="w-3 h-3" />
                                                      </span>
                                                    ) : (
                                                      <span
                                                        className="inline-flex items-center text-slate-400"
                                                        title="Manual entry"
                                                        data-testid={`team-source-manual-${entry.id}`}
                                                      >
                                                        <Pencil className="w-3 h-3" />
                                                      </span>
                                                    )}
                                                    <div className="min-w-0">
                                                      <p className="font-medium text-slate-900 text-sm truncate" title={entry.job_title || ''}>
                                                        {formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : '—')}
                                                      </p>
                                                      {entry.client_name && !shopMode && (
                                                        <p className="text-xs text-slate-500 truncate" title={entry.client_name}>
                                                          {entry.client_name}
                                                        </p>
                                                      )}
                                                      <EntryNotesLine
                                                        entry={entry}
                                                        displayedLabel={formatEntryLabel(entry, shopMode ? (entry.notes || 'Shop / Other') : '—')}
                                                      />
                                                    </div>
                                                  </div>

                                                  {/* Team + Time flow chips (4 cols) — Travel→ / On-Job / ←Travel */}
                                                  <div className="col-span-7 md:col-span-4 flex items-center gap-3 text-xs">
                                                    {entry.team_initials && (
                                                      <span className="font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[11px]">
                                                        {entry.team_initials}
                                                      </span>
                                                    )}
                                                    <div className="flex items-center gap-1.5 text-slate-600 flex-wrap">
                                                      {tTo > 0 && (
                                                        <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded" title="Travel To">
                                                          <span className="text-[10px] uppercase">→</span>
                                                          {formatMinutesToDisplay(tTo)}
                                                        </span>
                                                      )}
                                                      {tOn > 0 && (
                                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                                          shopMode ? 'bg-slate-100 text-slate-700' : 'bg-emerald-50 text-emerald-700'
                                                        }`} title={shopMode ? 'Shop / Other' : 'On Job'}>
                                                          {formatMinutesToDisplay(tOn)}
                                                        </span>
                                                      )}
                                                      {tFrom > 0 && (
                                                        <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded" title="Travel From">
                                                          <span className="text-[10px] uppercase">←</span>
                                                          {formatMinutesToDisplay(tFrom)}
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Total + Actions (3 cols) */}
                                                  <div className="col-span-5 md:col-span-3 flex items-center justify-end gap-3">
                                                    {entry.auto_source && (
                                                      <span
                                                        className="hidden lg:inline text-[11px] text-slate-400 italic whitespace-nowrap"
                                                        title={`Auto-generated from ${entry.auto_source.replace(/_/g, ' ')}`}
                                                      >
                                                        Auto-generated fr…
                                                      </span>
                                                    )}
                                                    <span className="font-bold text-slate-900 text-base whitespace-nowrap" title="Total">
                                                      {formatMinutesToDisplay(total)}
                                                    </span>
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                      {isLocked && (
                                                        <Lock className="w-3.5 h-3.5 text-emerald-600" aria-label="Locked" />
                                                      )}
                                                      <button
                                                        onClick={() => handleEditEntry(entry)}
                                                        disabled={isLocked}
                                                        className="text-slate-400 hover:text-amber-500 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                                                        title={isLocked ? `Locked — ${weekStatus}` : 'Edit'}
                                                      >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                      </button>
                                                      <button
                                                        onClick={() => handleDeleteEntry(entry.id, entry.user_id)}
                                                        disabled={isLocked}
                                                        className="text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                                                        title={isLocked ? `Locked — ${weekStatus}` : 'Delete'}
                                                      >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })()}
                        </div>
                      );
                    })()
                  ) : memberViewMode === 'grid' ? (
                    // ── WEEKLY GRID: jobs × days ──
                    (() => {
                      const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 });
                      const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(selectedWeek, { weekStartsOn: 0 }) });
                      // Pivot entries into job × dayIndex matrix
                      const matrix = {}; // jobKey -> { label, job_number, daily: [mins x 7], total }
                      teamEntries.forEach(e => {
                        const d = parseLocalDate(e.date);
                        const dayIdx = days.findIndex(dd => isSameDay(dd, d));
                        if (dayIdx < 0) return;
                        const key = e.job_id || `__custom__${e.job_title || e.notes || 'Other'}`;
                        if (!matrix[key]) {
                          matrix[key] = {
                            label: e.job_title || e.custom_job_description || 'Other',
                            job_number: e.job_number || '',
                            daily: [0, 0, 0, 0, 0, 0, 0],
                            entries: [[], [], [], [], [], [], []],
                            total: 0,
                          };
                        }
                        const mins = getEntryMinutes(e, 'total');
                        matrix[key].daily[dayIdx] += mins;
                        matrix[key].entries[dayIdx].push(e);
                        matrix[key].total += mins;
                      });
                      const rows = Object.values(matrix).sort((a, b) => b.total - a.total);
                      const dayTotals = [0, 0, 0, 0, 0, 0, 0];
                      rows.forEach(r => r.daily.forEach((m, i) => { dayTotals[i] += m; }));
                      const grandTotal = dayTotals.reduce((s, m) => s + m, 0);

                      const renderCell = (mins, entries, idx, row) => {
                        if (mins <= 0) return <span className="text-slate-300">—</span>;
                        const tooltip = entries.map(e => {
                          const parts = [];
                          if (e.start_time && e.end_time) {
                            try { parts.push(`${format(new Date(e.start_time), 'h:mma')} → ${format(new Date(e.end_time), 'h:mma')}`); } catch {}
                          }
                          const tt = getEntryMinutes(e, 'travel_to_job'), tj = getEntryMinutes(e, 'time_on_job'), tf = getEntryMinutes(e, 'travel_from_job');
                          if (tt) parts.push(`Travel to: ${formatMinutesToDisplay(tt)}`);
                          if (tj) parts.push(`On job: ${formatMinutesToDisplay(tj)}`);
                          if (tf) parts.push(`Travel from: ${formatMinutesToDisplay(tf)}`);
                          if (e.notes) parts.push(e.notes);
                          return parts.join(' • ');
                        }).join('\n');
                        return (
                          <span title={tooltip} className="text-slate-900 font-medium">
                            {formatMinutesToDisplay(mins)}
                          </span>
                        );
                      };

                      return (
                        <div className="overflow-x-auto" data-testid="member-week-grid">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="min-w-[180px]">Job</TableHead>
                                {days.map((d, i) => (
                                  <TableHead key={i} className="text-center min-w-[68px]">
                                    <div className="text-[10px] uppercase text-slate-400">{format(d, 'EEE')}</div>
                                    <div className="text-sm">{format(d, 'M/d')}</div>
                                  </TableHead>
                                ))}
                                <TableHead className="text-right min-w-[80px]">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rows.map((row, ri) => (
                                <TableRow key={ri}>
                                  <TableCell>
                                    {row.job_number && (
                                      <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded mr-2 whitespace-nowrap" title={row.job_number}>
                                        {formatJobBadge(row)}
                                      </span>
                                    )}
                                    <span className="text-sm text-slate-700">{titleCaseLabel(row.label)}</span>
                                  </TableCell>
                                  {row.daily.map((mins, di) => (
                                    <TableCell key={di} className="text-center text-sm">
                                      {renderCell(mins, row.entries[di], di, row)}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right font-semibold text-slate-900">
                                    {formatMinutesToDisplay(row.total)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="bg-slate-50">
                                <TableCell className="font-semibold text-slate-700">Daily Total</TableCell>
                                {dayTotals.map((m, i) => (
                                  <TableCell key={i} className="text-center text-sm font-semibold text-slate-900">
                                    {m > 0 ? formatMinutesToDisplay(m) : <span className="text-slate-300">—</span>}
                                  </TableCell>
                                ))}
                                <TableCell className="text-right font-bold text-amber-600">
                                  {formatMinutesToDisplay(grandTotal)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                          <p className="text-[11px] text-slate-400 mt-2 italic">Hover any cell for start/end times and travel/on-job breakdown.</p>
                        </div>
                      );
                    })()
                  ) : (
                    /* Team-view flat list — same grouped-by-day layout as
                       My Timesheet so admins read it the same way. */
                    teamEntries.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No time entries for this week</p>
                      </div>
                    ) : (() => {
                      const groups = {};
                      for (const e of teamEntries) {
                        const k = e.date;
                        if (!groups[k]) groups[k] = [];
                        groups[k].push(e);
                      }
                      // Sun → Sat (ascending) so approvers read the week like
                      // a normal calendar instead of reverse chronological.
                      const dates = Object.keys(groups).sort((a, b) => a.localeCompare(b));
                      // Within each day, sort morning → end of day.
                      for (const k of dates) {
                        groups[k].sort((a, b) =>
                          (a.start_time || a.created_at || '9999').localeCompare(b.start_time || b.created_at || '9999')
                        );
                      }
                      const isShopOther = (e) => {
                        const jn = (e.job_number || '').toUpperCase();
                        return jn.startsWith('OTHER') || jn.startsWith('SHOP-');
                      };
                      const weekTotal = teamEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                      return (
                        <div className="divide-y divide-slate-100">
                          {dates.map(dateKey => {
                            const dayEntries = groups[dateKey];
                            const dayMinutes = dayEntries.reduce((s, e) => s + getEntryMinutes(e, 'total'), 0);
                            const d = parseLocalDate(dateKey);
                            const isToday = format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                            return (
                              <div key={dateKey} data-testid={`team-entries-day-${dateKey}`}>
                                <div className={`flex items-baseline justify-between px-5 py-2.5 border-b border-slate-200 ${
                                  isToday ? 'bg-amber-50' : 'bg-slate-50/70'
                                }`}>
                                  <div className="flex items-baseline gap-3">
                                    <p className={`text-xs uppercase tracking-wider font-bold ${isToday ? 'text-amber-700' : 'text-slate-500'}`}>
                                      {format(d, 'EEEE')}
                                    </p>
                                    <p className={`text-lg font-bold font-['Barlow_Condensed'] leading-none ${isToday ? 'text-amber-700' : 'text-slate-900'}`}>
                                      {format(d, 'MMM d')}
                                    </p>
                                    <span className="text-xs text-slate-400">
                                      {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                                    </span>
                                  </div>
                                  <span className={`text-sm font-bold ${isToday ? 'text-amber-700' : 'text-slate-700'}`}>
                                    {formatMinutesToDisplay(dayMinutes)}
                                  </span>
                                </div>
                                <div className="divide-y divide-slate-50">
                                  {dayEntries.map(entry => {
                                    const tTo = getEntryMinutes(entry, 'travel_to_job');
                                    const tOn = getEntryMinutes(entry, 'time_on_job');
                                    const tFrom = getEntryMinutes(entry, 'travel_from_job');
                                    const total = getEntryMinutes(entry, 'total');
                                    const shopMode = isShopOther(entry);
                                    // Lock entries whose weekly envelope is Approved or Paid.
                                    // Backend already rejects edits via is_entry_locked() — this
                                    // surfaces the lock to the admin so they don't try and get
                                    // a confusing error toast.
                                    const weekStatus = selectedMemberWeekly?.status;
                                    const isLocked = weekStatus === 'Approved' || weekStatus === 'Paid';
                                    return (
                                      <div
                                        key={entry.id}
                                        className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-amber-50/30 transition-colors"
                                        data-testid={`team-entry-row-${entry.id}`}
                                      >
                                        <div className="col-span-12 md:col-span-5 min-w-0 flex items-start gap-2">
                                          {entry.job_number && (
                                            <span
                                              className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${
                                                shopMode
                                                  ? 'bg-slate-200 text-slate-700'
                                                  : 'bg-amber-50 text-amber-700'
                                              }`}
                                              title={entry.job_number}
                                            >
                                              {formatJobBadge(entry)}
                                            </span>
                                          )}
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-slate-900 whitespace-normal break-words leading-snug">
                                              {formatEntryLabel(entry, '—')}
                                            </p>
                                            {entry.client_name && (
                                              <p className="text-xs text-slate-500 truncate">{entry.client_name}</p>
                                            )}
                                            <EntryNotesLine
                                              entry={entry}
                                              displayedLabel={formatEntryLabel(entry, '—')}
                                            />
                                          </div>
                                        </div>
                                        <div className="col-span-7 md:col-span-4 flex flex-wrap gap-1">
                                          {tTo > 0 && (
                                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[11px] font-medium" title="Travel To">
                                              <span className="uppercase tracking-wider opacity-75">→</span>{formatMinutesToDisplay(tTo)}
                                            </span>
                                          )}
                                          {tOn > 0 && (
                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                              shopMode ? 'bg-slate-100 text-slate-700' : 'bg-emerald-50 text-emerald-700'
                                            }`} title={shopMode ? 'Shop / Other' : 'On Job'}>
                                              <span className="uppercase tracking-wider opacity-75">{shopMode ? 'Shop' : 'Job'}</span>{formatMinutesToDisplay(tOn)}
                                            </span>
                                          )}
                                          {tFrom > 0 && (
                                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[11px] font-medium" title="Travel From">
                                              <span className="uppercase tracking-wider opacity-75">←</span>{formatMinutesToDisplay(tFrom)}
                                            </span>
                                          )}
                                          {entry.notes && (
                                            <span className="text-xs text-slate-500 italic truncate" title={entry.notes}>
                                              · {entry.notes}
                                            </span>
                                          )}
                                        </div>
                                        <div className="col-span-3 md:col-span-2 text-right">
                                          <p className="text-sm font-bold text-slate-900 flex items-center justify-end gap-1">
                                            {isLocked && (
                                              <Lock
                                                className="w-3 h-3 text-emerald-600"
                                                aria-label="Locked — week is approved or paid"
                                                data-testid={`entry-lock-${entry.id}`}
                                              />
                                            )}
                                            {formatMinutesToDisplay(total)}
                                          </p>
                                          {entry.team_initials && (
                                            <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1 rounded">
                                              {entry.team_initials}
                                            </span>
                                          )}
                                        </div>
                                        <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-0.5">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEditEntry(entry)}
                                            disabled={isLocked}
                                            className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title={isLocked ? `Locked — ${weekStatus} (reopen the week to edit)` : 'Edit entry'}
                                            data-testid={`team-edit-${entry.id}`}
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteEntry(entry.id, entry.user_id)}
                                            disabled={isLocked}
                                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title={isLocked ? `Locked — ${weekStatus} (reopen the week to delete)` : 'Delete entry'}
                                            data-testid={`team-delete-${entry.id}`}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          <div className="flex items-center justify-between px-5 py-3 bg-slate-100 border-t border-slate-200">
                            <span className="text-sm font-semibold text-slate-700">Week Total</span>
                            <span className="text-lg font-bold text-amber-600 font-['Barlow_Condensed']">
                              {formatMinutesToDisplay(weekTotal)}
                            </span>
                          </div>
                        </div>
                      );
                    })()
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <>
          {/* Non-manager view — renders the SAME My Timesheet content the
              manager view uses (mini month calendar, owner-grade summary card,
              and the Day / Week / Calendar inline view via
              renderMyTimesheetContent()). Field workers were previously stuck
              with a stripped-down collapsed-card view that hid the day grid;
              they get the full experience now (just without the
              Team Timesheets tab). */}
          {/* Week Navigation */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-center">
                  <Button variant="outline" className="mr-4" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <div className="text-center px-4">
                    <h2 className="font-bold text-slate-900 text-lg">
                      Week {getWeek(weekStart)}, {weekStart.getFullYear()}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                    </p>
                  </div>
                  <Button variant="outline" className="ml-4" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                {/* Mini month widget — same as manager view. */}
                <MiniMonth
                  cursorMonth={miniMonthCursor}
                  selectedWeek={selectedWeek}
                  onPrevMonth={() => setMiniMonthCursor(subMonths(miniMonthCursor, 1))}
                  onNextMonth={() => setMiniMonthCursor(addMonths(miniMonthCursor, 1))}
                  onSelectDay={(d) => {
                    setSelectedWeek(d);
                    setMiniMonthCursor(d);
                  }}
                  onJumpToToday={() => {
                    const today = new Date();
                    setSelectedWeek(today);
                    setMiniMonthCursor(today);
                  }}
                />

                <Button
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold mx-auto"
                  onClick={() => setShowDetailDialog(true)}
                  data-testid="view-edit-timesheet-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  {entries.length > 0 ? 'View / Edit This Week\'s Timesheet' : 'Add Time Entries'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My Timesheets List */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-slate-900">My Timesheets</h3>
            </div>

            {/* Current Week Summary Card — owner-grade header.
                Same component the manager view uses. */}
            {(() => {
              const status = weeklyTimesheet?.status
                || (entries.length > 0 ? 'Draft' : null);
              const isReturned = weeklyTimesheet?.status === 'Draft' && weeklyTimesheet?.rejection_reason;
              const stripeColor = isReturned
                ? 'bg-amber-500'
                : status === 'Approved'
                  ? 'bg-emerald-500'
                  : status === 'Paid'
                    ? 'bg-slate-500'
                    : status === 'Submitted'
                      ? 'bg-blue-500'
                      : status === 'Draft'
                        ? 'bg-red-500'
                        : 'bg-slate-200';
              const statusLabel = isReturned ? 'Returned' : status || '—';
              return (
                <Card
                  className={`relative border-slate-200 hover:border-amber-300 transition-colors cursor-pointer overflow-hidden`}
                  onClick={() => setShowDetailDialog(true)}
                  data-testid="my-week-summary"
                >
                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripeColor}`} />
                  <CardContent className="p-4 pl-5">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                          Week {getWeek(weekStart)} · {weekStart.getFullYear()}
                        </p>
                        <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-tight">
                          {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`text-xs ${
                            isReturned ? 'bg-amber-100 text-amber-700'
                            : status === 'Approved' ? 'bg-emerald-100 text-emerald-700'
                            : status === 'Paid' ? 'bg-slate-200 text-slate-700'
                            : status === 'Submitted' ? 'bg-blue-100 text-blue-700'
                            : 'bg-red-50 text-red-700'
                          }`}>{statusLabel}</Badge>
                          <span className="text-xs text-slate-500">{entries.length} entries</span>
                          {weeklyTimesheet?.timesheet_number && (
                            <span className="text-xs text-slate-400 font-mono">{weeklyTimesheet.timesheet_number}</span>
                          )}
                        </div>
                        {isReturned && (
                          <p className="text-xs text-amber-600 mt-2">⚠ Needs correction: {weeklyTimesheet.rejection_reason}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] leading-none">
                          {formatMinutesToDisplay(totalMinutes)}
                        </p>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">Week Total</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Inline Day / Week view — Sun→Sat day cards + entries.
                Same as manager view. */}
            <div data-testid="my-timesheet-inline-view">
              {renderMyTimesheetContent()}
            </div>
          </div>
        </>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) setAddingForMember(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">
              {addingForMember ? (
                <span className="flex items-center gap-2">
                  Add Entry for 
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm"
                    style={{ backgroundColor: addingForMember.calendar_color || '#f59e0b', color: getContrastTextColor(addingForMember.calendar_color || '#f59e0b') }}
                  >
                    {addingForMember.user_name}
                  </span>
                </span>
              ) : (
                'Add Time Entry'
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Job / Activity *</Label>
              <Select
                value={newEntry.job_id}
                onValueChange={(v) => {
                  // Determine if the new selection is any shop category.
                  // Description field is preserved across shop selections
                  // and only cleared when switching to a real job.
                  const isShopCat = SHOP_CATEGORIES.some(c => c.id === v);
                  setNewEntry(prev => ({
                    ...prev,
                    job_id: v,
                    custom_job_description: isShopCat ? prev.custom_job_description : ''
                  }));
                }}
              >
                <SelectTrigger data-testid="job-select" className="w-full overflow-hidden">
                  <SelectValue placeholder="Select a job or activity" className="truncate" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-4rem)] md:max-w-md">
                  {/* Search input — sticky at the top so it stays visible
                      while scrolling the list. Stops propagation on key
                      events so Radix Select's built-in keyboard nav
                      doesn't hijack the typing. */}
                  <div className="sticky top-0 bg-white z-10 p-2 border-b border-slate-200">
                    <Input
                      autoFocus
                      data-testid="job-search-input"
                      placeholder="Search by customer, address, job # or keyword…"
                      value={jobSearchQuery}
                      onChange={(e) => setJobSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        // Prevent Radix from swallowing typing for its
                        // own first-letter navigation, and stop ArrowUp/
                        // Down from moving focus while user is searching.
                        e.stopPropagation();
                      }}
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Jobs Section — filtered by search query */}
                  {(() => {
                    const sourceJobs = addingForMember ? allJobs : jobs;
                    const filtered = filterJobsByQuery(sourceJobs, jobSearchQuery);
                    if (sourceJobs.length === 0) return null;
                    return (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                          JOBS {jobSearchQuery && (
                            <span className="font-normal text-slate-400">
                              · {filtered.length} of {sourceJobs.length}
                            </span>
                          )}
                        </div>
                        {filtered.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-slate-500 italic">
                            No jobs match &ldquo;{jobSearchQuery}&rdquo;. Try fewer or different keywords.
                          </div>
                        ) : (
                          filtered.map(job => (
                            <SelectItem key={job.id} value={job.id}>
                              <div className="flex items-center gap-2 max-w-full overflow-hidden">
                                {job.job_number && (
                                  <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                    {job.job_number}
                                  </span>
                                )}
                                <span className="truncate">{job.job_title}</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </>
                    );
                  })()}

                  {/* Shop Categories Section — never filtered; always visible */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 mt-1 border-t border-slate-200">
                    <Home className="w-3 h-3 inline mr-1" />
                    SHOP ACTIVITIES
                  </div>
                  {SHOP_CATEGORIES.map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <SelectItem key={cat.id} value={cat.id} data-testid={`shop-category-${cat.id}`}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span>{cat.label}</span>
                          {!cat.paid && (
                            <span className="text-xs text-slate-400 ml-1">(unpaid)</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {/* Show "Describe the activity" panel for ALL shop activities.
                  - Required for "Other" (it becomes the entry's job_title).
                  - Optional for the other categories (Shop Prep, Training,
                    Errands, Equipment, End of Day Cleanup, Lunch). The text
                    flows into the entry's `notes` field on save so it
                    appears in timesheet preview / reports — the canonical
                    activity label stays as "Shop Prep" etc. for grouping. */}
              {(() => {
                const selectedShopCat = SHOP_CATEGORIES.find(c => c.id === newEntry.job_id);
                if (!selectedShopCat) return null;
                const isOther = selectedShopCat.id === 'other';
                return (
                  <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 space-y-2">
                    <Label className="text-blue-700">
                      Describe this activity {isOther && <span className="text-red-600">*</span>}
                    </Label>
                    <Input
                      value={newEntry.custom_job_description || ''}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, custom_job_description: e.target.value }))}
                      placeholder={
                        isOther
                          ? 'e.g., Meeting, Office work, Administrative tasks…'
                          : `What did you work on during ${selectedShopCat.label}? (optional)`
                      }
                      className="bg-white"
                      data-testid="custom-job-description"
                    />
                    <p className="text-xs text-blue-600">
                      {isOther
                        ? 'Required — this label appears on your timesheet.'
                        : 'Optional — adds context to this entry on reports.'}
                    </p>
                  </div>
                );
              })()}
              
              {/* Show selected job info when a job is selected (not "other") */}
              {newEntry.job_id && newEntry.job_id !== 'other' && (
                <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200">
                  {(() => {
                    const jobList = addingForMember ? allJobs : jobs;
                    const selectedJob = jobList.find(j => j.id === newEntry.job_id);
                    return selectedJob ? (
                      <div className="text-sm min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-500">Selected:</span>
                          {selectedJob.job_number && (
                            <span className="font-mono font-bold text-amber-600">{selectedJob.job_number}</span>
                          )}
                        </div>
                        <p className="text-slate-700 font-medium truncate" title={selectedJob.job_title}>{selectedJob.job_title}</p>
                        <p className="text-slate-500 text-xs truncate" title={selectedJob.client_name}>{selectedJob.client_name}</p>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                className="bg-slate-50"
                data-testid="entry-date-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Travel To</Label>
                <Select
                  value={String(newEntry.travel_to_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, travel_to_job: v }))}
                >
                  <SelectTrigger className="bg-slate-50 text-xs sm:text-sm" data-testid="travel-to-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.travel_to_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">On Job *</Label>
                <Select
                  value={String(newEntry.time_on_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, time_on_job: v }))}
                >
                  <SelectTrigger className="bg-slate-50 text-xs sm:text-sm" data-testid="time-on-job-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.time_on_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Travel From</Label>
                <Select
                  value={String(newEntry.travel_from_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, travel_from_job: v }))}
                >
                  <SelectTrigger className="bg-slate-50 text-xs sm:text-sm" data-testid="travel-from-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.travel_from_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Pay Type</Label>
                <Select
                  value={newEntry.pay_type || 'Regular'}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, pay_type: v }))}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="pay-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Overtime">Overtime</SelectItem>
                    <SelectItem value="PTO">PTO</SelectItem>
                    <SelectItem value="Holiday">Holiday</SelectItem>
                    <SelectItem value="Sick">Sick</SelectItem>
                    <SelectItem value="Unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="bg-slate-50"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddEntry}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="save-entry-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) setEditingEntry(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">
              Edit Time Entry
              {editingEntry && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  for {editingEntry.user_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Job / Activity *</Label>
              <Select
                value={newEntry.job_id}
                onValueChange={(v) => {
                  const isShopCat = SHOP_CATEGORIES.some(c => c.id === v);
                  setNewEntry(prev => ({
                    ...prev,
                    job_id: v,
                    custom_job_description: isShopCat ? prev.custom_job_description : ''
                  }));
                }}
              >
                <SelectTrigger className="w-full overflow-hidden">
                  <SelectValue placeholder="Select a job or activity" className="truncate" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-4rem)] md:max-w-md">
                  {/* Search input — same UX as Add Entry dialog. */}
                  <div className="sticky top-0 bg-white z-10 p-2 border-b border-slate-200">
                    <Input
                      autoFocus
                      data-testid="job-search-input-edit"
                      placeholder="Search by customer, address, job # or keyword…"
                      value={jobSearchQuery}
                      onChange={(e) => setJobSearchQuery(e.target.value)}
                      onKeyDown={(e) => { e.stopPropagation(); }}
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* Jobs Section — filtered by search query */}
                  {(() => {
                    const filtered = filterJobsByQuery(allJobs, jobSearchQuery);
                    if (allJobs.length === 0) return null;
                    return (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                          JOBS {jobSearchQuery && (
                            <span className="font-normal text-slate-400">
                              · {filtered.length} of {allJobs.length}
                            </span>
                          )}
                        </div>
                        {filtered.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-slate-500 italic">
                            No jobs match &ldquo;{jobSearchQuery}&rdquo;. Try fewer or different keywords.
                          </div>
                        ) : (
                          filtered.map(job => (
                            <SelectItem key={job.id} value={job.id}>
                              <div className="flex items-center gap-2 max-w-full overflow-hidden">
                                {job.job_number && (
                                  <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                    {job.job_number}
                                  </span>
                                )}
                                <span className="truncate">{job.job_title}</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </>
                    );
                  })()}

                  {/* Shop Categories Section — never filtered; always visible */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 mt-1 border-t border-slate-200">
                    <Home className="w-3 h-3 inline mr-1" />
                    SHOP ACTIVITIES
                  </div>
                  {SHOP_CATEGORIES.map(cat => {
                    const IconComponent = cat.icon;
                    return (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span>{cat.label}</span>
                          {!cat.paid && (
                            <span className="text-xs text-slate-400 ml-1">(unpaid)</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {(() => {
                const selectedShopCat = SHOP_CATEGORIES.find(c => c.id === newEntry.job_id);
                if (!selectedShopCat) return null;
                const isOther = selectedShopCat.id === 'other';
                return (
                  <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 space-y-2">
                    <Label className="text-blue-700">
                      Describe this activity {isOther && <span className="text-red-600">*</span>}
                    </Label>
                    <Input
                      value={newEntry.custom_job_description || ''}
                      onChange={(e) => setNewEntry(prev => ({ ...prev, custom_job_description: e.target.value }))}
                      placeholder={
                        isOther
                          ? 'e.g., Meeting, Office work, Administrative tasks…'
                          : `What did you work on during ${selectedShopCat.label}? (optional)`
                      }
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600">
                      {isOther
                        ? 'Required — this label appears on your timesheet.'
                        : 'Optional — adds context to this entry on reports.'}
                    </p>
                  </div>
                );
              })()}
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                className="bg-slate-50"
              />
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Travel To</Label>
                <Select
                  value={String(newEntry.travel_to_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, travel_to_job: parseFloat(v) }))}
                >
                  <SelectTrigger className="text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.travel_to_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">On Job *</Label>
                <Select
                  value={String(newEntry.time_on_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, time_on_job: parseFloat(v) }))}
                >
                  <SelectTrigger className="text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.time_on_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Travel From</Label>
                <Select
                  value={String(newEntry.travel_from_job)}
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, travel_from_job: parseFloat(v) }))}
                >
                  <SelectTrigger className="text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getHourOptionsForValue(newEntry.travel_from_job).map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="bg-slate-50"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateEntry}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timesheet Detail Dialog - Like Expense Reports */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap text-base md:text-lg">
              <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-500 shrink-0" />
              <span>Week {getWeek(weekStart)}, {weekStart.getFullYear()}</span>
              {weeklyTimesheet ? (
                <>
                  {weeklyTimesheet.status === 'Draft' && !weeklyTimesheet.rejection_reason && (
                    <Badge className="bg-slate-100 text-slate-700 text-xs">Draft</Badge>
                  )}
                  {weeklyTimesheet.status === 'Submitted' && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Submitted</Badge>
                  )}
                  {weeklyTimesheet.status === 'Approved' && (
                    <Badge className="bg-green-100 text-green-700 text-xs">Approved</Badge>
                  )}
                  {weeklyTimesheet.status === 'Paid' && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs border border-emerald-300">Paid</Badge>
                  )}
                  {weeklyTimesheet.status === 'Draft' && weeklyTimesheet.rejection_reason && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">Returned</Badge>
                  )}
                </>
              ) : entries.length > 0 ? (
                <Badge className="bg-slate-100 text-slate-700 text-xs">Draft</Badge>
              ) : null}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {format(weekStart, 'MMM d')} to {format(weekEnd, 'MMM d, yyyy')}
            </p>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* User Info */}
            <div className="p-3 md:p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-amber-100 shrink-0">
                  <span className="text-amber-700 font-bold text-base md:text-lg">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{user?.full_name}</p>
                  <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-xl font-bold text-amber-600">{formatMinutesToDisplay(totalMinutes)}</p>
                  {weeklyTimesheet?.timesheet_number && (
                    <p className="text-xs text-slate-500 font-mono">{weeklyTimesheet.timesheet_number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Rejection Banner */}
            {weeklyTimesheet?.rejection_reason && weeklyTimesheet?.status === 'Draft' && (
              <div className="p-3 sm:p-4 bg-amber-50 rounded-lg border border-amber-300">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800">Timesheet Returned for Corrections</p>
                    <p className="text-sm text-amber-700 mt-1">{weeklyTimesheet.rejection_reason}</p>
                    {weeklyTimesheet.rejected_by && (
                      <p className="text-xs text-amber-600 mt-2">
                        Returned by {weeklyTimesheet.rejected_by}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Time Entries List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-slate-700">Time Entries ({entries.length})</h4>
                <div className="flex items-center gap-2">
                  <div className="inline-flex h-8 border border-slate-200 rounded-md overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setMyTimesheetViewMode('day')}
                      className={`px-2.5 h-full text-xs ${myTimesheetViewMode === 'day' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                      data-testid="my-view-day-dialog"
                    >
                      Day View
                    </button>
                    <button
                      type="button"
                      onClick={() => setMyTimesheetViewMode('week')}
                      className={`px-2.5 h-full text-xs border-l border-slate-200 ${myTimesheetViewMode === 'week' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'}`}
                      data-testid="my-view-week-dialog"
                    >
                      Week View
                    </button>
                  </div>
                  {(isSuperUser || !weeklyTimesheet || weeklyTimesheet.status === 'Draft') && (
                    <Button 
                      size="sm"
                      onClick={() => { setShowDetailDialog(false); setShowAddDialog(true); }}
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Entry
                    </Button>
                  )}
                </div>
              </div>
              
              {entries.length === 0 ? (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No time entries for this week</p>
                  <Button 
                    className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-900"
                    onClick={() => { setShowDetailDialog(false); setShowAddDialog(true); }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Entry
                  </Button>
                </div>
              ) : myTimesheetViewMode === 'week' ? (
                renderMyWeekSpreadsheet()
              ) : (
                <div className="space-y-2">
                  {entries.map(entry => (
                    <div 
                      key={entry.id}
                      className="p-3 bg-white border rounded-lg hover:border-amber-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-900">
                              {format(parseLocalDate(entry.date), 'EEE, MMM d')}
                            </span>
                            {entry.job_number && entry.job_number !== 'OTHER' && (
                              <span className="font-mono text-xs font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap" title={entry.job_number}>
                                {formatJobBadge(entry)}
                              </span>
                            )}
                            {entry.job_number === 'OTHER' && (
                              <span className="text-xs text-slate-500 italic">Other</span>
                            )}
                          </div>
                          {/* Client and Job Title */}
                          {entry.client_name && (
                            <p className="text-xs text-slate-500 mt-1 truncate" title={entry.client_name}>
                              {entry.client_name}
                            </p>
                          )}
                          <p className="text-sm text-slate-700 mt-0.5 truncate" title={entry.job_title}>
                            {formatEntryLabel(entry, 'Job')}
                          </p>
                          <EntryNotesLine
                            entry={entry}
                            displayedLabel={formatEntryLabel(entry, 'Job')}
                          />
                          {/* Contact and Team */}
                          <div className="flex flex-wrap gap-2 mt-1 text-xs">
                            {entry.contact_name && (
                              <span className="text-slate-500">Contact: {entry.contact_name}</span>
                            )}
                            {entry.team_initials && (
                              <span className="font-mono bg-slate-100 text-slate-600 px-1 rounded">{entry.team_initials}</span>
                            )}
                          </div>
                          {/* Start/End timestamps from Time Tracker V2 */}
                          {(entry.start_time || entry.end_time) && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-500" data-testid={`entry-timestamps-${entry.id}`}>
                              <Clock className="w-3 h-3" />
                              <span>
                                {entry.start_time ? format(new Date(entry.start_time), 'h:mm a') : '?'}
                                {' → '}
                                {entry.end_time ? format(new Date(entry.end_time), 'h:mm a') : '?'}
                              </span>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                            {getEntryMinutes(entry, 'travel_to_job') > 0 && (
                              <span>Travel to: {formatMinutesToDisplay(getEntryMinutes(entry, 'travel_to_job'))}</span>
                            )}
                            <span className="font-medium text-slate-700">On job: {formatMinutesToDisplay(getEntryMinutes(entry, 'time_on_job'))}</span>
                            {getEntryMinutes(entry, 'travel_from_job') > 0 && (
                              <span>Travel from: {formatMinutesToDisplay(getEntryMinutes(entry, 'travel_from_job'))}</span>
                            )}
                          </div>
                          {entry.notes && (
                            <p className="text-xs text-slate-500 mt-2 truncate">{entry.notes}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-lg font-bold text-amber-600">{formatMinutesToDisplay(getEntryMinutes(entry, 'total'))}</span>
                          {(isSuperUser || !weeklyTimesheet || weeklyTimesheet.status === 'Draft') && (
                            <div className="flex items-center gap-3">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setShowDetailDialog(false); handleEditEntry(entry); }}
                                className="h-7 px-2 text-slate-500 hover:text-amber-600"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="h-7 px-2 text-slate-400 hover:text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Total */}
            <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="font-semibold text-slate-700">Total Hours</span>
              <span className="text-xl font-bold text-amber-600">{formatMinutesToDisplay(totalMinutes)}</span>
            </div>

            {/* Submission Status Info */}
            {weeklyTimesheet && (
              <div className={`p-3 rounded-lg border ${
                weeklyTimesheet.status === 'Submitted' ? 'bg-blue-50 border-blue-200' :
                weeklyTimesheet.status === 'Approved' ? 'bg-green-50 border-green-200' :
                weeklyTimesheet.status === 'Paid' ? 'bg-emerald-50 border-emerald-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {weeklyTimesheet.status === 'Submitted' && <AlertCircle className="w-5 h-5 text-blue-500" />}
                    {weeklyTimesheet.status === 'Approved' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                    {weeklyTimesheet.status === 'Paid' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                    {weeklyTimesheet.status === 'Draft' && <Clock className="w-5 h-5 text-slate-500" />}
                    <span className="font-medium">
                      {weeklyTimesheet.status === 'Submitted' && 'Submitted - Awaiting Approval'}
                      {weeklyTimesheet.status === 'Approved' && `Approved by ${weeklyTimesheet.approved_by}`}
                      {weeklyTimesheet.status === 'Paid' && `Paid via ${weeklyTimesheet.payment_method}`}
                      {weeklyTimesheet.status === 'Draft' && 'Draft - Not Submitted'}
                    </span>
                  </div>
                  {weeklyTimesheet.submitted_at && (
                    <span className="text-xs text-slate-500">
                      {format(parseISO(weeklyTimesheet.submitted_at), 'MMM d, h:mm a')}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* No Submission Yet - Show how to submit */}
            {!weeklyTimesheet && entries.length > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700">
                  <Send className="w-5 h-5" />
                  <span className="font-medium">Ready to Submit!</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Click the green "Submit for Approval" button below to send this timesheet for review.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              {/* Submit Button - Show when no submission or Draft status */}
              {(!weeklyTimesheet || weeklyTimesheet.status === 'Draft') && entries.length > 0 && (
                <Button 
                  onClick={() => { handleSubmitTimesheet(); }}
                  disabled={submitting}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  data-testid="submit-timesheet-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Submit for Approval
                </Button>
              )}
              
              {/* Reset Week Button - Super User Only */}
              {isSuperUser && weeklyTimesheet && (
                <Button 
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  disabled={resetting}
                  onClick={() => setShowResetConfirm(true)}
                >
                  {resetting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Reset Week
                </Button>
              )}
              
              <Button variant="outline" onClick={handleExportCSV}>
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              {weeklyTimesheet?.id && (
                <Button 
                  onClick={() => { setShowDetailDialog(false); navigate(`/timesheet/${weeklyTimesheet.id}/preview`); }} 
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Week Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Timesheet for Week {getWeek(weekStart)}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the timesheet submission status. Your time entries will be preserved, 
              but you'll need to submit again for approval. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={async () => {
                if (!weeklyTimesheet) return;
                setResetting(true);
                try {
                  await axios.delete(
                    `${API}/timesheets/weekly/${weeklyTimesheet.id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success('Timesheet reset - you can now submit fresh');
                  setWeeklyTimesheet(null);
                  setShowDetailDialog(false);
                  setShowResetConfirm(false);
                  fetchWeeklyTimesheetStatus();
                } catch (error) {
                  console.error('Reset error:', error);
                  toast.error(error.response?.data?.detail || 'Failed to reset timesheet');
                } finally {
                  setResetting(false);
                }
              }}
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Reset Week
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
